import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Database,
    Building2,
    Users,
    GraduationCap,
    Plus,
    Edit,
    Trash2,
    ToggleLeft,
    ToggleRight,
    X,
    AlertTriangle,
    RefreshCw,
    Search,
    SearchX,
    Eye,
    ArrowUp,
    ArrowDown,
    ArrowUpDown,
    FileText,
    Calendar,
    Download,
    Briefcase,
    Phone,
    Heart,
    HelpCircle,
    Mail
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../../context/AuthContext';
import Button from '../../../components/common/Button/Button';
import ExportModal from '../../../components/common/ExportModal';
import LeadInterestManagement from './LeadInterestManagement';
import CompanySizeManagement from './CompanySizeManagement';
import PaginationControls from '../../../components/common/Pagination/PaginationControls';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import { industryService, decisionMakerService, courseTypeService, documentTypeService, regularizationReasonService, leaveTypeService, holidayTypeService, policyCategoryService, sourceService, leadSourceService, leadDocumentTypeService, leadCategoryService, activityStatusService, leadStatusService, leadVideoStatusService, leadOrderStatusService, leadCallStatusService, experienceService, salaryComponentService, skillService, requirementService, learningPathCategoryService, candidateAvailabilityService, designationService, internalTicketTypeService, internalApprovalCategoryService } from '../../../api/crm/config/configService';
import { internalTicketService } from '../../../api/crm/support/internalSupportService';
import { formatDate } from '../../../utils/stringUtils';
import { exportToCSV, downloadFromBackend, generateExportFilename } from '../../../utils/exportUtils';

const NAME_MIN_LENGTH = 2;
const NAME_MAX_LENGTH = 50;
const DOC_NAME_MAX_LENGTH = 100;
const DOC_DESC_MAX_LENGTH = 200;

// Custom debounce hook
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);
    return debouncedValue;
};

const ConfigureManagement = ({ module }) => {
    const queryClient = useQueryClient();
    const { user } = useAuth();
    const isSuperAdmin = user?.role === 'super_admin';

    const [settingsSaving, setSettingsSaving] = useState(false);

    // Ticket Settings State
    const [ticketSettingsData, setTicketSettingsData] = useState({
        adminEmail: '',
        fallbackEmail: ''
    });

    const [activeTab, setActiveTab] = useState(module === 'crm' ? 'industry' : 'courseType');
    const [modalType, setModalType] = useState('create'); // 'create', 'edit'
    const [modalFor, setModalFor] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({ name: '', description: '', isActive: true, minYear: 0, maxYear: 0, noMax: false, order: 1 });
    const [formErrors, setFormErrors] = useState({});
    const [editingItem, setEditingItem] = useState(null);
    const [exportLoading, setExportLoading] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
    const [selectedRows, setSelectedRows] = useState(new Set());

    // Define allowed tabs based on module
    const allowedTabs = module === 'crm'
        ? ['industry', 'decisionMaker', 'leadSource', 'leadDocumentType', 'leadCategory', 'activityStatus', 'leadStatus', 'leadVideoStatus', 'leadOrderStatus', 'leadCallStatus', 'leadInterest', 'companySize', 'internalTicketType', 'internalApprovalCategory', ...(isSuperAdmin ? ['ticketSettings'] : [])]
        : ['courseType', 'documentType', 'leaveType', 'holidayType', 'policyCategory', 'source', 'experience', 'salaryComponent', 'regularizationReason', 'skill', 'requirement', 'learningPathCategory', 'candidateAvailability', 'designation'];

    const defaultTab = module === 'crm' ? 'industry' : 'courseType';

    const [showViewModal, setShowViewModal] = useState(false);
    const [viewingItem, setViewingItem] = useState(null);

    // Delete confirmation modal
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deletingItem, setDeletingItem] = useState(null);
    const [deleteError, setDeleteError] = useState('');

    // Sorting state
    const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });

    // Search state
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
    const debouncedSearch = useDebounce(searchTerm, 300);

    // When the module prop changes (e.g. navigating between /hrms_configuration and /crm_configuration),
    // reset the active tab to the first tab of the new module.
    useEffect(() => {
        setActiveTab(defaultTab);
        setSearchTerm('');
        setStatusFilter('all');
        setDateRange({ startDate: '', endDate: '' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [module]);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageLimit, setPageLimit] = useState(10);

    // Reset page 1 when tab/filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [activeTab, debouncedSearch, statusFilter, dateRange]);

    // --- Queries for Totals (fetch all without filters) ---
    // Each query is gated by allowedTabs so CRM totals don't fire on HRMS module and vice versa.
    const { data: allIndustries } = useQuery({
        queryKey: ['industries', 'all'],
        queryFn: () => industryService.getAll({}),
        enabled: allowedTabs.includes('industry')
    });
    const totalIndustries = allIndustries?.data?.length || 0;

    const { data: allDecisionMakers } = useQuery({
        queryKey: ['decisionMakers', 'all'],
        queryFn: () => decisionMakerService.getAll({}),
        enabled: allowedTabs.includes('decisionMaker')
    });
    const totalDecisionMakers = allDecisionMakers?.data?.length || 0;

    const { data: allCourseTypes } = useQuery({
        queryKey: ['courseTypes', 'all'],
        queryFn: () => courseTypeService.getAll({}),
        enabled: allowedTabs.includes('courseType')
    });
    const totalCourseTypes = allCourseTypes?.data?.length || 0;

    const { data: allDocumentTypes } = useQuery({
        queryKey: ['documentTypes', 'all'],
        queryFn: () => documentTypeService.getAll({}),
        enabled: allowedTabs.includes('documentType')
    });
    const totalDocumentTypes = allDocumentTypes?.data?.length || 0;

    const { data: allRegularizationReasons } = useQuery({
        queryKey: ['regularizationReasons', 'all'],
        queryFn: () => regularizationReasonService.getAll({}),
        enabled: allowedTabs.includes('regularizationReason')
    });
    const totalRegularizationReasons = allRegularizationReasons?.data?.length || 0;

    const { data: allLeaveTypes } = useQuery({
        queryKey: ['leaveTypes', 'all'],
        queryFn: () => leaveTypeService.getAll({}),
        enabled: allowedTabs.includes('leaveType')
    });
    const totalLeaveTypes = allLeaveTypes?.data?.length || 0;

    const { data: allHolidayTypes } = useQuery({
        queryKey: ['holidayTypes', 'all'],
        queryFn: () => holidayTypeService.getAll({}),
        enabled: allowedTabs.includes('holidayType')
    });
    const totalHolidayTypes = allHolidayTypes?.data?.length || 0;

    const { data: allSources = [] } = useQuery({
        queryKey: ['sources', 'all'],
        queryFn: () => sourceService.getAll(),
        enabled: allowedTabs.includes('source')
    });

    const { data: allLeadSources = [] } = useQuery({
        queryKey: ['leadSources', 'all'],
        queryFn: () => leadSourceService.getAll(),
        enabled: allowedTabs.includes('leadSource')
    });
    const totalSources = allSources?.data?.length || 0;


    const { data: allExperiences } = useQuery({
        queryKey: ['experiences', 'all'],
        queryFn: () => experienceService.getAll({}),
        enabled: allowedTabs.includes('experience')
    });
    const totalExperiences = allExperiences?.data?.length || 0;


    const { data: allSalaryComponents } = useQuery({
        queryKey: ['salaryComponents', 'all'],
        queryFn: () => salaryComponentService.getAll({}),
        enabled: allowedTabs.includes('salaryComponent')
    });
    const totalSalaryComponents = allSalaryComponents?.data?.length || 0;

    // Skills query
    const { data: allSkills } = useQuery({
        queryKey: ['skills', 'all'],
        queryFn: () => skillService.getAll({}),
        enabled: allowedTabs.includes('skill')
    });
    const totalSkills = allSkills?.data?.length || 0;

    const { data: allRequirements } = useQuery({
        queryKey: ['requirements', 'all'],
        queryFn: () => requirementService.getAll({}),
        enabled: allowedTabs.includes('requirement')
    });
    const totalRequirements = allRequirements?.data?.length || 0;

    const { data: allLearningPathCategories } = useQuery({
        queryKey: ['learningPathCategories', 'all'],
        queryFn: () => learningPathCategoryService.getAll({}),
        enabled: allowedTabs.includes('learningPathCategory')
    });
    const totalLearningPathCategories = allLearningPathCategories?.data?.length || 0;

    const { data: allCandidateAvailabilities } = useQuery({
        queryKey: ['candidateAvailabilities', 'all'],
        queryFn: () => candidateAvailabilityService.getAll({}),
        enabled: allowedTabs.includes('candidateAvailability')
    });
    const totalCandidateAvailabilities = allCandidateAvailabilities?.data?.length || 0;



    // --- Queries for Active Tab Data (with filters) ---
    const { data: industriesData, isLoading: loadingIndustries } = useQuery({
        queryKey: ['industries', { search: debouncedSearch, status: statusFilter, startDate: dateRange.startDate, endDate: dateRange.endDate }],
        queryFn: () => industryService.getAll({
            search: debouncedSearch,
            ...(statusFilter !== 'all' && { isActive: statusFilter === 'active' }),
            startDate: dateRange.startDate,
            endDate: dateRange.endDate
        }),
        enabled: activeTab === 'industry'
    });
    const industries = industriesData?.data || [];

    const { data: decisionMakersData, isLoading: loadingDecisionMakers } = useQuery({
        queryKey: ['decisionMakers', { search: debouncedSearch, status: statusFilter, startDate: dateRange.startDate, endDate: dateRange.endDate }],
        queryFn: () => decisionMakerService.getAll({
            search: debouncedSearch,
            ...(statusFilter !== 'all' && { isActive: statusFilter === 'active' }),
            startDate: dateRange.startDate,
            endDate: dateRange.endDate
        }),
        enabled: activeTab === 'decisionMaker'
    });
    const decisionMakers = decisionMakersData?.data || [];

    const { data: courseTypesData, isLoading: loadingCourseTypes } = useQuery({
        queryKey: ['courseTypes', { search: debouncedSearch, status: statusFilter, startDate: dateRange.startDate, endDate: dateRange.endDate }],
        queryFn: () => courseTypeService.getAll({
            search: debouncedSearch,
            ...(statusFilter !== 'all' && { isActive: statusFilter === 'active' }),
            startDate: dateRange.startDate,
            endDate: dateRange.endDate
        }),
        enabled: activeTab === 'courseType'
    });
    const courseTypes = courseTypesData?.data || [];

    const { data: designationsData, isLoading: loadingDesignations } = useQuery({
        queryKey: ['designations', { search: debouncedSearch, status: statusFilter, startDate: dateRange.startDate, endDate: dateRange.endDate }],
        queryFn: () => designationService.getAll({
            search: debouncedSearch,
            ...(statusFilter !== 'all' && { isActive: statusFilter === 'active' }),
            startDate: dateRange.startDate,
            endDate: dateRange.endDate
        }),
        enabled: activeTab === 'designation'
    });
    const designations = useMemo(() => designationsData?.data || [], [designationsData]);

    const { data: documentTypesData, isLoading: loadingDocumentTypes } = useQuery({
        queryKey: ['documentTypes', { search: debouncedSearch, status: statusFilter, startDate: dateRange.startDate, endDate: dateRange.endDate }],
        queryFn: () => documentTypeService.getAll({
            search: debouncedSearch,
            ...(statusFilter !== 'all' && { isActive: statusFilter === 'active' }),
            startDate: dateRange.startDate,
            endDate: dateRange.endDate
        }),
        enabled: activeTab === 'documentType'
    });
    const documentTypes = documentTypesData?.data || [];

    const { data: requirementsData, isLoading: loadingRequirements } = useQuery({
        queryKey: ['requirements', { search: debouncedSearch, status: statusFilter, startDate: dateRange.startDate, endDate: dateRange.endDate }],
        queryFn: () => requirementService.getAll({
            search: debouncedSearch,
            ...(statusFilter !== 'all' && { isActive: statusFilter === 'active' }),
            startDate: dateRange.startDate,
            endDate: dateRange.endDate
        }),
        enabled: activeTab === 'requirement'
    });
    const requirements = requirementsData?.data || [];

    const { data: regularizationReasonsData, isLoading: loadingRegularizationReasons } = useQuery({
        queryKey: ['regularizationReasons', { search: debouncedSearch, status: statusFilter, startDate: dateRange.startDate, endDate: dateRange.endDate }],
        queryFn: () => regularizationReasonService.getAll({
            search: debouncedSearch,
            ...(statusFilter !== 'all' && { isActive: statusFilter === 'active' }),
            startDate: dateRange.startDate,
            endDate: dateRange.endDate
        }),
        enabled: activeTab === 'regularizationReason'
    });
    const regularizationReasons = regularizationReasonsData?.data || [];

    const { data: leaveTypesData, isLoading: loadingLeaveTypes } = useQuery({
        queryKey: ['leaveTypes', { search: debouncedSearch, status: statusFilter, startDate: dateRange.startDate, endDate: dateRange.endDate }],
        queryFn: () => leaveTypeService.getAll({
            search: debouncedSearch,
            ...(statusFilter !== 'all' && { isActive: statusFilter === 'active' }),
            startDate: dateRange.startDate,
            endDate: dateRange.endDate
        }),
        enabled: activeTab === 'leaveType'
    });
    const leaveTypes = leaveTypesData?.data || [];

    const { data: holidayTypesData, isLoading: loadingHolidayTypes } = useQuery({
        queryKey: ['holidayTypes', { search: debouncedSearch, status: statusFilter, startDate: dateRange.startDate, endDate: dateRange.endDate }],
        queryFn: () => holidayTypeService.getAll({
            search: debouncedSearch,
            ...(statusFilter !== 'all' && { isActive: statusFilter === 'active' }),
            startDate: dateRange.startDate,
            endDate: dateRange.endDate
        }),
        enabled: activeTab === 'holidayType'
    });
    const holidayTypes = holidayTypesData?.data || [];

    const { data: policyCategoriesData, isLoading: loadingPolicyCategories } = useQuery({
        queryKey: ['policyCategories', { search: debouncedSearch, status: statusFilter, startDate: dateRange.startDate, endDate: dateRange.endDate }],
        queryFn: () => policyCategoryService.getAll({
            search: debouncedSearch,
            ...(statusFilter !== 'all' && { isActive: statusFilter === 'active' }),
            startDate: dateRange.startDate,
            endDate: dateRange.endDate
        }),
        enabled: activeTab === 'policyCategory'
    });
    const policyCategories = policyCategoriesData?.data || [];

    const { data: sourcesData, isLoading: loadingSources } = useQuery({
        queryKey: ['sources', { search: debouncedSearch, status: statusFilter, startDate: dateRange.startDate, endDate: dateRange.endDate }],
        queryFn: () => sourceService.getAll({
            search: debouncedSearch,
            ...(statusFilter !== 'all' && { isActive: statusFilter === 'active' }),
            startDate: dateRange.startDate,
            endDate: dateRange.endDate
        }),
        enabled: activeTab === 'source'
    });
    const sources = sourcesData?.data || [];

    const { data: leadSourcesData, isLoading: loadingLeadSources } = useQuery({
        queryKey: ['leadSources', { search: debouncedSearch, status: statusFilter, startDate: dateRange.startDate, endDate: dateRange.endDate }],
        queryFn: () => leadSourceService.getAll({
            search: debouncedSearch,
            ...(statusFilter !== 'all' && { isActive: statusFilter === 'active' }),
            startDate: dateRange.startDate,
            endDate: dateRange.endDate
        }),
        enabled: activeTab === 'leadSource'
    });
    const leadSources = leadSourcesData?.data || [];

    const { data: leadDocumentTypesData, isLoading: loadingLeadDocumentTypes } = useQuery({
        queryKey: ['leadDocumentTypes', { search: debouncedSearch, status: statusFilter, startDate: dateRange.startDate, endDate: dateRange.endDate }],
        queryFn: () => leadDocumentTypeService.getAll({
            search: debouncedSearch,
            ...(statusFilter !== 'all' && { isActive: statusFilter === 'active' }),
            startDate: dateRange.startDate,
            endDate: dateRange.endDate
        }),
        enabled: activeTab === 'leadDocumentType'
    });
    const leadDocumentTypes = leadDocumentTypesData?.data || [];

    const { data: leadCategoriesData, isLoading: loadingLeadCategories } = useQuery({
        queryKey: ['leadCategories', { search: debouncedSearch, status: statusFilter, startDate: dateRange.startDate, endDate: dateRange.endDate }],
        queryFn: () => leadCategoryService.getAll({
            search: debouncedSearch,
            ...(statusFilter !== 'all' && { isActive: statusFilter === 'active' }),
            startDate: dateRange.startDate,
            endDate: dateRange.endDate
        }),
        enabled: activeTab === 'leadCategory'
    });
    const leadCategories = leadCategoriesData?.data || [];

    const { data: activityStatusesData, isLoading: loadingActivityStatuses } = useQuery({
        queryKey: ['activityStatuses', { search: debouncedSearch, status: statusFilter, startDate: dateRange.startDate, endDate: dateRange.endDate }],
        queryFn: () => activityStatusService.getAll({
            search: debouncedSearch,
            ...(statusFilter !== 'all' && { isActive: statusFilter === 'active' }),
            startDate: dateRange.startDate,
            endDate: dateRange.endDate
        }),
        enabled: activeTab === 'activityStatus'
    });
    const activityStatuses = activityStatusesData?.data || [];

    const { data: leadStatusesData, isLoading: loadingLeadStatuses } = useQuery({
        queryKey: ['leadStatuses', { search: debouncedSearch, status: statusFilter, startDate: dateRange.startDate, endDate: dateRange.endDate }],
        queryFn: () => leadStatusService.getAll({
            search: debouncedSearch,
            ...(statusFilter !== 'all' && { isActive: statusFilter === 'active' }),
            startDate: dateRange.startDate,
            endDate: dateRange.endDate
        }),
        enabled: activeTab === 'leadStatus'
    });
    const leadStatuses = leadStatusesData?.data || [];

    const { data: leadVideoStatusesData, isLoading: loadingLeadVideoStatuses } = useQuery({
        queryKey: ['leadVideoStatuses', { search: debouncedSearch, status: statusFilter, startDate: dateRange.startDate, endDate: dateRange.endDate }],
        queryFn: () => leadVideoStatusService.getAll({
            search: debouncedSearch,
            ...(statusFilter !== 'all' && { isActive: statusFilter === 'active' }),
            startDate: dateRange.startDate,
            endDate: dateRange.endDate
        }),
        enabled: activeTab === 'leadVideoStatus'
    });
    const leadVideoStatuses = leadVideoStatusesData?.data || [];

    const { data: leadOrderStatusesData, isLoading: loadingLeadOrderStatuses } = useQuery({
        queryKey: ['leadOrderStatuses', { search: debouncedSearch, status: statusFilter, startDate: dateRange.startDate, endDate: dateRange.endDate }],
        queryFn: () => leadOrderStatusService.getAll({
            search: debouncedSearch,
            ...(statusFilter !== 'all' && { isActive: statusFilter === 'active' }),
            startDate: dateRange.startDate,
            endDate: dateRange.endDate
        }),
        enabled: activeTab === 'leadOrderStatus'
    });
    const leadOrderStatuses = leadOrderStatusesData?.data || [];

    const { data: leadCallStatusesData, isLoading: loadingLeadCallStatuses } = useQuery({
        queryKey: ['leadCallStatuses', { search: debouncedSearch, status: statusFilter, startDate: dateRange.startDate, endDate: dateRange.endDate }],
        queryFn: () => leadCallStatusService.getAll({
            search: debouncedSearch,
            ...(statusFilter !== 'all' && { isActive: statusFilter === 'active' }),
            startDate: dateRange.startDate,
            endDate: dateRange.endDate
        }),
        enabled: activeTab === 'leadCallStatus'
    });
    const leadCallStatuses = leadCallStatusesData?.data || [];

    const { data: experiencesData, isLoading: loadingExperiences } = useQuery({
        queryKey: ['experiences', { search: debouncedSearch, status: statusFilter, startDate: dateRange.startDate, endDate: dateRange.endDate }],
        queryFn: () => experienceService.getAll({
            search: debouncedSearch,
            ...(statusFilter !== 'all' && { isActive: statusFilter === 'active' }),
            startDate: dateRange.startDate,
            endDate: dateRange.endDate
        }),
        enabled: activeTab === 'experience'
    });
    const experiences = experiencesData?.data || [];
    const { data: salaryComponentsData, isLoading: loadingSalaryComponents } = useQuery({
        queryKey: ['salaryComponents', { search: debouncedSearch, status: statusFilter, startDate: dateRange.startDate, endDate: dateRange.endDate }],
        queryFn: () => salaryComponentService.getAll({
            search: debouncedSearch,
            ...(statusFilter !== 'all' && { isActive: statusFilter === 'active' }),
            startDate: dateRange.startDate,
            endDate: dateRange.endDate
        }),
        enabled: activeTab === 'salaryComponent'
    });
    const salaryComponents = salaryComponentsData?.data || [];

    const { data: skillsData, isLoading: loadingSkills } = useQuery({
        queryKey: ['skills', { search: debouncedSearch, status: statusFilter, startDate: dateRange.startDate, endDate: dateRange.endDate }],
        queryFn: () => skillService.getAll({
            search: debouncedSearch,
            ...(statusFilter !== 'all' && { isActive: statusFilter === 'active' }),
            startDate: dateRange.startDate,
            endDate: dateRange.endDate
        }),
        enabled: activeTab === 'skill'
    });
    const skills = skillsData?.data || [];

    const { data: learningPathCategoriesData, isLoading: loadingLearningPathCategories } = useQuery({
        queryKey: ['learningPathCategories', { search: debouncedSearch, status: statusFilter, startDate: dateRange.startDate, endDate: dateRange.endDate }],
        queryFn: () => learningPathCategoryService.getAll({
            search: debouncedSearch,
            ...(statusFilter !== 'all' && { isActive: statusFilter === 'active' }),
            startDate: dateRange.startDate,
            endDate: dateRange.endDate
        }),
        enabled: activeTab === 'learningPathCategory'
    });
    const learningPathCategories = learningPathCategoriesData?.data || [];

    const { data: candidateAvailabilitiesData, isLoading: loadingCandidateAvailabilities } = useQuery({
        queryKey: ['candidateAvailabilities', { search: debouncedSearch, status: statusFilter, startDate: dateRange.startDate, endDate: dateRange.endDate }],
        queryFn: () => candidateAvailabilityService.getAll({
            search: debouncedSearch,
            ...(statusFilter !== 'all' && { isActive: statusFilter === 'active' }),
            startDate: dateRange.startDate,
            endDate: dateRange.endDate
        }),
        enabled: activeTab === 'candidateAvailability'
    });
    const candidateAvailabilities = candidateAvailabilitiesData?.data || [];

    const { data: internalTicketTypesData, isLoading: loadingInternalTicketTypes } = useQuery({
        queryKey: ['internalTicketTypes', { search: debouncedSearch, status: statusFilter, startDate: dateRange.startDate, endDate: dateRange.endDate }],
        queryFn: () => internalTicketTypeService.getAll({
            search: debouncedSearch,
            ...(statusFilter !== 'all' && { isActive: statusFilter === 'active' }),
            startDate: dateRange.startDate,
            endDate: dateRange.endDate
        }),
        enabled: activeTab === 'internalTicketType'
    });
    const internalTicketTypes = internalTicketTypesData?.data || [];

    const { data: allInternalTicketTypes } = useQuery({
        queryKey: ['internalTicketTypes', 'all'],
        queryFn: () => internalTicketTypeService.getAll({}),
        enabled: allowedTabs.includes('internalTicketType')
    });
    const totalInternalTicketTypes = allInternalTicketTypes?.data?.length || 0;

    const { data: internalApprovalCategoriesData, isLoading: loadingInternalApprovalCategories } = useQuery({
        queryKey: ['internalApprovalCategories', { search: debouncedSearch, status: statusFilter, startDate: dateRange.startDate, endDate: dateRange.endDate }],
        queryFn: () => internalApprovalCategoryService.getAll({
            search: debouncedSearch,
            ...(statusFilter !== 'all' && { isActive: statusFilter === 'active' }),
            startDate: dateRange.startDate,
            endDate: dateRange.endDate
        }),
        enabled: activeTab === 'internalApprovalCategory'
    });
    const internalApprovalCategories = internalApprovalCategoriesData?.data || [];

    const { data: allInternalApprovalCategories } = useQuery({
        queryKey: ['internalApprovalCategories', 'all'],
        queryFn: () => internalApprovalCategoryService.getAll({}),
        enabled: allowedTabs.includes('internalApprovalCategory')
    });
    const totalInternalApprovalCategories = allInternalApprovalCategories?.data?.length || 0;

    const { data: ticketSettingsResponse, refetch: refetchTicketSettings } = useQuery({
        queryKey: ['ticketSettings'],
        queryFn: () => internalTicketService.getSettings(),
        enabled: activeTab === 'ticketSettings' && isSuperAdmin
    });

    useEffect(() => {
        if (ticketSettingsResponse?.success && ticketSettingsResponse?.data) {
            const adminEmails = ticketSettingsResponse.data.adminEmails || [];
            setTicketSettingsData({
                adminEmail: adminEmails[0] || '',
                fallbackEmail: adminEmails[1] || ''
            });
        }
    }, [ticketSettingsResponse]);

    // --- Mutations ---
    const createMutation = useMutation({
        mutationFn: ({ service, data }) => service.create(data),
        onSuccess: (response, { itemType, queryKeyBase }) => {
            if (response.success) {
                toast.success(response.responseMessage || response.message || `${itemType} created successfully`);
                queryClient.invalidateQueries({
                    predicate: (query) => query.queryKey[0] === queryKeyBase && typeof query.queryKey[1] === 'object' && query.queryKey[1] !== null && !Array.isArray(query.queryKey[1])
                });
                closeModal();
            } else if (modalFor === 'experience') {
                toast.error(response.message || 'Failed to create item');
            }
        },
        onError: (error) => {
            const errorMsg = error.message || 'Failed to create item';
            setFormErrors({ name: errorMsg });
            if (modalFor === 'experience') {
                toast.error(errorMsg);
            }
        }
    });

    const updateMutation = useMutation({
        mutationFn: ({ service, id, data }) => service.update(id, data),
        onSuccess: (response, { itemType, queryKeyBase }) => {
            if (response.success) {
                toast.success(response.responseMessage || response.message || `${itemType} updated successfully`);
                queryClient.invalidateQueries({
                    predicate: (query) => query.queryKey[0] === queryKeyBase && typeof query.queryKey[1] === 'object' && query.queryKey[1] !== null && !Array.isArray(query.queryKey[1])
                });
                closeModal();
            } else if (modalFor === 'experience') {
                toast.error(response.message || 'Failed to update item');
            }
        },
        onError: (error) => {
            const errorMsg = error.message || 'Failed to update item';
            setFormErrors({ name: errorMsg });
            if (modalFor === 'experience') {
                toast.error(errorMsg);
            }
        }
    });

    const toggleStatusMutation = useMutation({
        mutationFn: ({ service, id }) => service.toggleStatus(id),
        onSuccess: (response, { queryKeyBase }) => {
            if (response.success) {
                toast.success(response.responseMessage || response.message);
                queryClient.invalidateQueries({
                    predicate: (query) => query.queryKey[0] === queryKeyBase && typeof query.queryKey[1] === 'object' && query.queryKey[1] !== null && !Array.isArray(query.queryKey[1])
                });
            } else if (activeTab === 'experience') {
                toast.error(response.message || 'Failed to toggle status');
            }
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to toggle status');
        }
    });

    const deleteMutation = useMutation({
        mutationFn: ({ service, id }) => service.delete(id),
        onSuccess: (response, { queryKeyBase }) => {
            if (response.success) {
                toast.success(response.responseMessage || response.message);
                queryClient.invalidateQueries({
                    predicate: (query) => query.queryKey[0] === queryKeyBase && typeof query.queryKey[1] === 'object' && query.queryKey[1] !== null && !Array.isArray(query.queryKey[1])
                });
                closeDeleteModal();
            } else {
                toast.error(response.message || 'Failed to delete item');
            }
        },
        onError: (error) => {
            const errorMsg = error.message || 'Failed to delete item';
            setDeleteError(errorMsg);
            toast.error(errorMsg);
        }
    });

    const bulkDeleteMutation = useMutation({
        mutationFn: ({ service, ids }) => service.bulkDelete(ids),
        onSuccess: (response, { queryKeyBase }) => {
            if (response.success) {
                toast.success(response.message || 'Items deleted successfully');
                queryClient.invalidateQueries({
                    predicate: (query) => query.queryKey[0] === queryKeyBase && typeof query.queryKey[1] === 'object' && query.queryKey[1] !== null && !Array.isArray(query.queryKey[1])
                });
                setShowBulkDeleteModal(false);
                setSelectedRows(new Set());
            } else {
                toast.error(response.message || 'Failed to delete items');
            }
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to delete items');
        }
    });



    // Sorting handlers
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortIndicator = (key) => {
        if (sortConfig.key !== key) return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
        return sortConfig.direction === 'asc' ?
            <ArrowUp className="h-4 w-4 text-agrosaf-green" /> :
            <ArrowDown className="h-4 w-4 text-agrosaf-green" />;
    };

    // Validation function
    const validateName = (name, type) => {
        const errors = {};
        const trimmedName = name?.trim() || '';
        const limit = (type === 'designation' || type === 'documentType' || type === 'leaveType' || type === 'holidayType' || type === 'policyCategory' || type === 'source' || type === 'leadSource' || type === 'industry' || type === 'decisionMaker' || type === 'experience' || type === 'salaryComponent' || type === 'leadDocumentType' || type === 'leadCategory' || type === 'activityStatus' || type === 'skill' || type === 'requirement' || type === 'learningPathCategory' || type === 'internalTicketType' || type === 'internalApprovalCategory') ? DOC_NAME_MAX_LENGTH : NAME_MAX_LENGTH;

        if (!trimmedName) {
            errors.name = 'Name is required';
        } else if (trimmedName.length < NAME_MIN_LENGTH) {
            errors.name = `Name must be at least ${NAME_MIN_LENGTH} characters`;
        } else if (trimmedName.length > limit) {
            errors.name = `Name cannot exceed ${limit} characters`;
        } else if (!/^[a-zA-Z0-9\s\-& +/(),.]+$/.test(trimmedName)) {
            errors.name = 'Name can only contain letters, numbers, spaces, hyphens, ampersands, plus signs, forward slashes, parentheses, commas, and dots';
        }

        if (type === 'experience') {
            if (!formData.noMax && formData.minYear > formData.maxYear) {
                errors.minYear = 'Min cannot be > Max';
                errors.maxYear = 'Max cannot be < Min';
            }
        }

        if (type === 'designation') {
            const parsedOrder = Number(formData.order);
            if (!Number.isFinite(parsedOrder)) {
                errors.order = 'Order is required';
            } else if (parsedOrder < 1) {
                errors.order = 'Order must start from 1';
            }
        }

        return errors;
    };

    const handleBlur = (field) => {
        if (field === 'name' || field === 'minYear' || field === 'maxYear') {
            const errors = validateName(formData.name, modalFor);
            setFormErrors(prev => ({ ...prev, ...errors }));
        }
    };

    // Modal handlers
    const openCreateModal = (type) => {
        setModalType('create');
        setModalFor(type);
        setFormData({ name: '', description: '', isActive: true, minYear: 0, maxYear: 0, noMax: false, order: 1, value: '' });
        setFormErrors({});
        setEditingItem(null);
        setShowModal(true);
    };

    const openEditModal = (item, type) => {
        setModalType('edit');
        setModalFor(type);
        let minYear = 0;
        let maxYear = 0;
        let noMax = false;
        if (type === 'experience') {
            if (item.minYear !== undefined) minYear = item.minYear;
            if (item.maxYear !== undefined) maxYear = item.maxYear;
            if (item.noMaxLimit !== undefined) noMax = item.noMaxLimit;

            if (minYear === 0 && maxYear === 0 && !noMax && item.name) {
                const rangeMatch = item.name.match(/^(\d+)-(\d+) Years$/);
                const plusMatch = item.name.match(/^(\d+)\+ Years$/);

                if (rangeMatch) {
                    minYear = parseInt(rangeMatch[1], 10);
                    maxYear = parseInt(rangeMatch[2], 10);
                } else if (plusMatch) {
                    minYear = parseInt(plusMatch[1], 10);
                    noMax = true;
                }
            }
        }
        setFormData({
            name: item.name,
            description: item.description || '',
            isActive: item.isActive !== undefined ? item.isActive : true,
            minYear,
            maxYear,
            noMax,
            type: item.type || 'earning',
            order: item.order ?? 1,
            value: item.value || ''
        });
        setFormErrors({});
        setEditingItem(item);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setFormData({ name: '', description: '', isActive: true, minYear: 0, maxYear: 0, noMax: false, order: 1, value: '' });
        setFormErrors({});
        setEditingItem(null);
    };

    const openViewModal = (item, type) => {
        setViewingItem({ ...item, type });
        setShowViewModal(true);
    };

    const closeViewModal = () => {
        setShowViewModal(false);
        setViewingItem(null);
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();

        // Validation
        const errors = validateName(formData.name, modalFor);
        if (modalFor === 'leadStatus' && modalType === 'create' && !formData.value?.trim()) {
            errors.value = 'Value key is required';
        }
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            if (modalFor === 'experience') {
                Object.values(errors).forEach(err => toast.error(err));
            }
            return;
        }

        let service;
        let itemType;
        let queryKeyBase;

        switch (modalFor) {
            case 'internalTicketType':
                service = internalTicketTypeService;
                itemType = 'Ticket Type';
                queryKeyBase = 'internalTicketTypes';
                break;
            case 'internalApprovalCategory':
                service = internalApprovalCategoryService;
                itemType = 'Approval Category';
                queryKeyBase = 'internalApprovalCategories';
                break;
            case 'industry':
                service = industryService;
                itemType = 'Industry';
                queryKeyBase = 'industries';
                break;
            case 'decisionMaker':
                service = decisionMakerService;
                itemType = 'Decision Maker';
                queryKeyBase = 'decisionMakers';
                break;
            case 'courseType':
                service = courseTypeService;
                itemType = 'Course Type';
                queryKeyBase = 'courseTypes';
                break;
            case 'designation':
                service = designationService;
                itemType = 'Designation';
                queryKeyBase = 'designations';
                break;
            case 'documentType':
                service = documentTypeService;
                itemType = 'Document Type';
                queryKeyBase = 'documentTypes';
                break;
            case 'regularizationReason':
                service = regularizationReasonService;
                itemType = 'Regularization Reason';
                queryKeyBase = 'regularizationReasons';
                break;
            case 'leaveType':
                service = leaveTypeService;
                itemType = 'Leave Type';
                queryKeyBase = 'leaveTypes';
                break;
            case 'holidayType':
                service = holidayTypeService;
                itemType = 'Holiday Type';
                queryKeyBase = 'holidayTypes';
                break;
            case 'policyCategory':
                service = policyCategoryService;
                itemType = 'Policy Category';
                queryKeyBase = 'policyCategories';
                break;
            case 'source':
                service = sourceService;
                itemType = 'Source';
                queryKeyBase = 'sources';
                break;
            case 'leadSource':
                service = leadSourceService;
                itemType = 'Lead Source';
                queryKeyBase = 'leadSources';
                break;
            case 'leadDocumentType':
                service = leadDocumentTypeService;
                itemType = 'Lead Document Type';
                queryKeyBase = 'leadDocumentTypes';
                break;
            case 'leadCategory':
                service = leadCategoryService;
                itemType = 'Lead Category';
                queryKeyBase = 'leadCategories';
                break;
            case 'activityStatus':
                service = activityStatusService;
                itemType = 'Activity Status';
                queryKeyBase = 'activityStatuses';
                break;
            case 'leadStatus':
                service = leadStatusService;
                itemType = 'Lead Status';
                queryKeyBase = 'leadStatuses';
                break;
            case 'leadVideoStatus':
                service = leadVideoStatusService;
                itemType = 'Lead Video Status';
                queryKeyBase = 'leadVideoStatuses';
                break;
            case 'leadOrderStatus':
                service = leadOrderStatusService;
                itemType = 'Lead Order Status';
                queryKeyBase = 'leadOrderStatuses';
                break;
            case 'leadCallStatus':
                service = leadCallStatusService;
                itemType = 'Lead Call Status';
                queryKeyBase = 'leadCallStatuses';
                break;
            case 'experience':
                service = experienceService;
                itemType = 'Experience';
                queryKeyBase = 'experiences';
                break;
            case 'salaryComponent':
                service = salaryComponentService;
                itemType = 'Salary Component';
                queryKeyBase = 'salaryComponents';
                break;
            case 'skill':
                service = skillService;
                itemType = 'Skill';
                queryKeyBase = 'skills';
                break;
            case 'requirement':
                service = requirementService;
                itemType = 'Requirement';
                queryKeyBase = 'requirements';
                break;
            case 'learningPathCategory':
                service = learningPathCategoryService;
                itemType = 'Learning Path Category';
                queryKeyBase = 'learningPathCategories';
                break;
            case 'candidateAvailability':
                service = candidateAvailabilityService;
                itemType = 'Candidate Availability';
                queryKeyBase = 'candidateAvailabilities';
                break;
            default:
                throw new Error('Invalid item type');
        }

        const submitData = {
            name: formData.name.trim(),
            ...((modalFor === 'designation' || modalFor === 'courseType' || modalFor === 'documentType' || modalFor === 'regularizationReason' || modalFor === 'leaveType' || modalFor === 'holidayType' || modalFor === 'policyCategory' || modalFor === 'source' || modalFor === 'leadSource' || modalFor === 'leadDocumentType' || modalFor === 'leadCategory' || modalFor === 'activityStatus' || modalFor === 'leadStatus' || modalFor === 'leadVideoStatus' || modalFor === 'leadOrderStatus' || modalFor === 'industry' || modalFor === 'decisionMaker' || modalFor === 'experience' || modalFor === 'salaryComponent' || modalFor === 'skill' || modalFor === 'requirement' || modalFor === 'learningPathCategory' || modalFor === 'candidateAvailability' || modalFor === 'internalTicketType' || modalFor === 'internalApprovalCategory') && {
                description: formData.description?.trim() || '',
                isActive: formData.isActive
            }),
            ...(modalFor === 'designation' && {
                order: Number(formData.order)
            }),
            ...(modalFor === 'experience' && {
                minYear: formData.minYear,
                maxYear: formData.maxYear,
                noMaxLimit: formData.noMax
            }),
            ...(modalFor === 'leadStatus' && {
                value: formData.value?.trim()?.toLowerCase() || ''
            })
        };

        if (modalType === 'create') {
            createMutation.mutate({ service, data: submitData, itemType, queryKeyBase });
        } else {
            updateMutation.mutate({ service, id: editingItem._id || editingItem.id, data: submitData, itemType, queryKeyBase });
        }
    };

    // Toggle status handler
    const handleToggleStatus = (item, type) => {
        let service;
        let queryKeyBase;
        switch (type) {
            case 'internalTicketType':
                service = internalTicketTypeService;
                queryKeyBase = 'internalTicketTypes';
                break;
            case 'internalApprovalCategory':
                service = internalApprovalCategoryService;
                queryKeyBase = 'internalApprovalCategories';
                break;
            case 'industry':
                service = industryService;
                queryKeyBase = 'industries';
                break;
            case 'decisionMaker':
                service = decisionMakerService;
                queryKeyBase = 'decisionMakers';
                break;
            case 'courseType':
                service = courseTypeService;
                queryKeyBase = 'courseTypes';
                break;
            case 'designation':
                service = designationService;
                queryKeyBase = 'designations';
                break;
            case 'documentType':
                service = documentTypeService;
                queryKeyBase = 'documentTypes';
                break;
            case 'regularizationReason':
                service = regularizationReasonService;
                queryKeyBase = 'regularizationReasons';
                break;
            case 'leaveType':
                service = leaveTypeService;
                queryKeyBase = 'leaveTypes';
                break;
            case 'holidayType':
                service = holidayTypeService;
                queryKeyBase = 'holidayTypes';
                break;
            case 'policyCategory':
                service = policyCategoryService;
                queryKeyBase = 'policyCategories';
                break;
            case 'source':
                service = sourceService;
                queryKeyBase = 'sources';
                break;
            case 'leadSource':
                service = leadSourceService;
                queryKeyBase = 'leadSources';
                break;
            case 'leadDocumentType':
                service = leadDocumentTypeService;
                queryKeyBase = 'leadDocumentTypes';
                break;
            case 'leadCategory':
                service = leadCategoryService;
                queryKeyBase = 'leadCategories';
                break;
            case 'activityStatus':
                service = activityStatusService;
                queryKeyBase = 'activityStatuses';
                break;
            case 'leadStatus':
                service = leadStatusService;
                queryKeyBase = 'leadStatuses';
                break;
            case 'leadVideoStatus':
                service = leadVideoStatusService;
                queryKeyBase = 'leadVideoStatuses';
                break;
            case 'leadOrderStatus':
                service = leadOrderStatusService;
                queryKeyBase = 'leadOrderStatuses';
                break;
            case 'leadCallStatus':
                service = leadCallStatusService;
                queryKeyBase = 'leadCallStatuses';
                break;
            case 'experience':
                service = experienceService;
                queryKeyBase = 'experiences';
                break;
            case 'salaryComponent':
                service = salaryComponentService;
                queryKeyBase = 'salaryComponents';
                break;
            case 'skill':
                service = skillService;
                queryKeyBase = 'skills';
                break;
            case 'requirement':
                service = requirementService;
                queryKeyBase = 'requirements';
                break;
            case 'learningPathCategory':
                service = learningPathCategoryService;
                queryKeyBase = 'learningPathCategories';
                break;
            case 'candidateAvailability':
                service = candidateAvailabilityService;
                queryKeyBase = 'candidateAvailabilities';
                break;
            default: return;
        }

        toggleStatusMutation.mutate({ service, id: item._id || item.id, queryKeyBase });
    };

    // Delete handlers
    const openDeleteModal = (item, type) => {
        setDeletingItem({ ...item, type });
        setDeleteError('');
        setShowDeleteModal(true);
    };

    const closeDeleteModal = () => {
        setShowDeleteModal(false);
        setDeletingItem(null);
        setDeleteError('');
    };

    const handleDelete = () => {
        if (!deletingItem) return;

        let service;
        let queryKeyBase;
        switch (deletingItem.type) {
            case 'internalTicketType':
                service = internalTicketTypeService;
                queryKeyBase = 'internalTicketTypes';
                break;
            case 'industry':
                service = industryService;
                queryKeyBase = 'industries';
                break;
            case 'decisionMaker':
                service = decisionMakerService;
                queryKeyBase = 'decisionMakers';
                break;
            case 'courseType':
                service = courseTypeService;
                queryKeyBase = 'courseTypes';
                break;
            case 'designation':
                service = designationService;
                queryKeyBase = 'designations';
                break;
            case 'documentType':
                service = documentTypeService;
                queryKeyBase = 'documentTypes';
                break;
            case 'regularizationReason':
                service = regularizationReasonService;
                queryKeyBase = 'regularizationReasons';
                break;
            case 'leaveType':
                service = leaveTypeService;
                queryKeyBase = 'leaveTypes';
                break;
            case 'holidayType':
                service = holidayTypeService;
                queryKeyBase = 'holidayTypes';
                break;
            case 'policyCategory':
                service = policyCategoryService;
                queryKeyBase = 'policyCategories';
                break;
            case 'source':
                service = sourceService;
                queryKeyBase = 'sources';
                break;
            case 'leadSource':
                service = leadSourceService;
                queryKeyBase = 'leadSources';
                break;
            case 'leadDocumentType':
                service = leadDocumentTypeService;
                queryKeyBase = 'leadDocumentTypes';
                break;
            case 'leadCategory':
                service = leadCategoryService;
                queryKeyBase = 'leadCategories';
                break;
            case 'activityStatus':
                service = activityStatusService;
                queryKeyBase = 'activityStatuses';
                break;
            case 'leadStatus':
                service = leadStatusService;
                queryKeyBase = 'leadStatuses';
                break;
            case 'leadVideoStatus':
                service = leadVideoStatusService;
                queryKeyBase = 'leadVideoStatuses';
                break;
            case 'leadOrderStatus':
                service = leadOrderStatusService;
                queryKeyBase = 'leadOrderStatuses';
                break;
            case 'leadCallStatus':
                service = leadCallStatusService;
                queryKeyBase = 'leadCallStatuses';
                break;
            case 'experience':
                service = experienceService;
                queryKeyBase = 'experiences';
                break;
            case 'salaryComponent':
                service = salaryComponentService;
                queryKeyBase = 'salaryComponents';
                break;
            case 'skill':
                service = skillService;
                queryKeyBase = 'skills';
                break;
            case 'requirement':
                service = requirementService;
                queryKeyBase = 'requirements';
                break;
            case 'learningPathCategory':
                service = learningPathCategoryService;
                queryKeyBase = 'learningPathCategories';
                break;
            case 'candidateAvailability':
                service = candidateAvailabilityService;
                queryKeyBase = 'candidateAvailabilities';
                break;
            case 'internalApprovalCategory':
                service = internalApprovalCategoryService;
                queryKeyBase = 'internalApprovalCategories';
                break;
            default: return;
        }

        deleteMutation.mutate({ service, id: deletingItem._id || deletingItem.id, queryKeyBase });
    };

    const handleBulkDelete = () => {
        if (selectedRows.size === 0) return;

        let service;
        let queryKeyBase;
        switch (activeTab) {
            case 'internalTicketType':
                service = internalTicketTypeService;
                queryKeyBase = 'internalTicketTypes';
                break;
            case 'industry':
                service = industryService;
                queryKeyBase = 'industries';
                break;
            case 'decisionMaker':
                service = decisionMakerService;
                queryKeyBase = 'decisionMakers';
                break;
            case 'courseType':
                service = courseTypeService;
                queryKeyBase = 'courseTypes';
                break;
            case 'designation':
                service = designationService;
                queryKeyBase = 'designations';
                break;
            case 'documentType':
                service = documentTypeService;
                queryKeyBase = 'documentTypes';
                break;
            case 'regularizationReason':
                service = regularizationReasonService;
                queryKeyBase = 'regularizationReasons';
                break;
            case 'leaveType':
                service = leaveTypeService;
                queryKeyBase = 'leaveTypes';
                break;
            case 'holidayType':
                service = holidayTypeService;
                queryKeyBase = 'holidayTypes';
                break;
            case 'policyCategory':
                service = policyCategoryService;
                queryKeyBase = 'policyCategories';
                break;
            case 'source':
                service = sourceService;
                queryKeyBase = 'sources';
                break;
            case 'leadSource':
                service = leadSourceService;
                queryKeyBase = 'leadSources';
                break;
            case 'leadDocumentType':
                service = leadDocumentTypeService;
                queryKeyBase = 'leadDocumentTypes';
                break;
            case 'leadCategory':
                service = leadCategoryService;
                queryKeyBase = 'leadCategories';
                break;
            case 'activityStatus':
                service = activityStatusService;
                queryKeyBase = 'activityStatuses';
                break;
            case 'leadStatus':
                service = leadStatusService;
                queryKeyBase = 'leadStatuses';
                break;
            case 'leadVideoStatus':
                service = leadVideoStatusService;
                queryKeyBase = 'leadVideoStatuses';
                break;
            case 'leadOrderStatus':
                service = leadOrderStatusService;
                queryKeyBase = 'leadOrderStatuses';
                break;
            case 'leadCallStatus':
                service = leadCallStatusService;
                queryKeyBase = 'leadCallStatuses';
                break;
            case 'experience':
                service = experienceService;
                queryKeyBase = 'experiences';
                break;
            case 'salaryComponent':
                service = salaryComponentService;
                queryKeyBase = 'salaryComponents';
                break;
            case 'skill':
                service = skillService;
                queryKeyBase = 'skills';
                break;
            case 'requirement':
                service = requirementService;
                queryKeyBase = 'requirements';
                break;
            case 'learningPathCategory':
                service = learningPathCategoryService;
                queryKeyBase = 'learningPathCategories';
                break;
            case 'candidateAvailability':
                service = candidateAvailabilityService;
                queryKeyBase = 'candidateAvailabilities';
                break;
            case 'internalApprovalCategory':
                service = internalApprovalCategoryService;
                queryKeyBase = 'internalApprovalCategories';
                break;
            default: return;
        }

        bulkDeleteMutation.mutate({
            service,
            ids: Array.from(selectedRows),
            queryKeyBase
        });
    };

    // Clear search
    const handleClearSearch = () => {
        setSearchTerm('');
    };

    // Character count helper
    const getCharacterCount = () => {
        const count = formData.name?.length || 0;
        const limit = (modalFor === 'designation' || modalFor === 'documentType' || modalFor === 'leaveType' || modalFor === 'holidayType' || modalFor === 'policyCategory' || modalFor === 'source' || modalFor === 'leadSource' || modalFor === 'leadDocumentType' || modalFor === 'industry' || modalFor === 'decisionMaker' || modalFor === 'experience' || modalFor === 'salaryComponent' || modalFor === 'skill' || modalFor === 'requirement' || modalFor === 'learningPathCategory' || modalFor === 'internalTicketType' || modalFor === 'internalApprovalCategory') ? DOC_NAME_MAX_LENGTH : NAME_MAX_LENGTH;
        return `${count}/${limit}`;
    };

    // --- Export Functionality ---

    // Reset selection when tab or filters change
    useEffect(() => {
        setSelectedRows(new Set());
    }, [activeTab, searchTerm, statusFilter]);

    // Helper to get current tab data
    const getCurrentTabData = useCallback(() => {
        switch (activeTab) {
            case 'industry': return industries;
            case 'decisionMaker': return decisionMakers;
            case 'designation': return designations;
            case 'courseType': return courseTypes;
            case 'documentType': return documentTypes;
            case 'regularizationReason': return regularizationReasons;
            case 'leaveType': return leaveTypes;
            case 'holidayType': return holidayTypes;
            case 'policyCategory': return policyCategories;
            case 'source': return sources;
            case 'leadSource': return leadSources;
            case 'leadDocumentType': return leadDocumentTypes;
            case 'leadCategory': return leadCategories;
            case 'activityStatus': return activityStatuses;
            case 'leadStatus': return leadStatuses;
            case 'leadVideoStatus': return leadVideoStatuses;
            case 'leadOrderStatus': return leadOrderStatuses;
            case 'leadCallStatus': return leadCallStatuses;
            case 'experience': return experiences;
            case 'salaryComponent': return salaryComponents;
            case 'skill': return skills;
            case 'requirement': return requirements;
            case 'learningPathCategory': return learningPathCategories;
            case 'candidateAvailability': return candidateAvailabilities;
            case 'internalTicketType': return internalTicketTypes;
            case 'internalApprovalCategory': return internalApprovalCategories;
            default: return [];
        }
    }, [activeTab, industries, decisionMakers, designations, courseTypes, documentTypes, regularizationReasons, leaveTypes, holidayTypes, policyCategories, sources, leadSources, leadDocumentTypes, leadCategories, activityStatuses, leadStatuses, leadVideoStatuses, leadOrderStatuses, leadCallStatuses, experiences, salaryComponents, skills, requirements, learningPathCategories, candidateAvailabilities, internalTicketTypes, internalApprovalCategories, searchTerm, statusFilter]);

    const handleSelectAll = (e) => {
        // Find current pagination data
        const sourceData = getCurrentTabData();
        const sorted = [...sourceData].sort((a, b) => { /* use original sorting logic later, or simplify */ return 0; });
        // Getting paginatedData for checkbox is complex if we recompute, 
        // to simplify, just select ALL filtered data across pages or let handleSelectAll be defined cleanly?
        // Actually, since this is standard, let's select all FILTERED data (the sourceData) instead of just paginatedData.

        if (e.target.checked) {
            const allIds = sourceData.map(item => item._id || item.id);
            setSelectedRows(new Set(allIds));
        } else {
            setSelectedRows(new Set());
        }
    };

    const handleSelectRow = (id) => {
        const newSelected = new Set(selectedRows);
        if (newSelected.has(id)) {
            newSelected.delete(id);
        } else {
            newSelected.add(id);
        }
        setSelectedRows(newSelected);
    };

    // Helper to get item date (fallback to _id timestamp if createdAt is missing)
    const getItemDate = (item) => {
        if (item.createdAt) return new Date(item.createdAt);
        if (item._id || item.id) {
            const id = item._id || item.id;
            // MongoDB ObjectIds are 24-char hex strings containing a timestamp in the first 8 chars
            if (typeof id === 'string' && id.length === 24) {
                const timestamp = parseInt(id.substring(0, 8), 16) * 1000;
                return new Date(timestamp);
            }
        }
        return null; // Should not happen for valid MongoDB docs
    };

    const mapDataForExport = (data) => {
        return data.map(item => {
            const createDate = getItemDate(item);
            const updateDate = item.updatedAt ? new Date(item.updatedAt) : null;
            const base = {
                "ID": activeTab === 'leadStatus' ? (item.value || '-') : (item[activeTab === 'industry' ? 'industryCode' : activeTab === 'decisionMaker' ? 'decisionMakerCode' : activeTab === 'designation' ? 'designationCode' : activeTab === 'courseType' ? 'courseTypeId' : activeTab === 'documentType' ? 'documentTypeCode' : activeTab === 'regularizationReason' ? 'reasonCode' : activeTab === 'leaveType' ? 'leaveTypeCode' : activeTab === 'holidayType' ? 'holidayTypeCode' : activeTab === 'policyCategory' ? 'code' : activeTab === 'source' ? 'sourceCode' : activeTab === 'leadSource' ? 'leadSourceCode' : activeTab === 'leadDocumentType' ? 'leadDocumentTypeCode' : activeTab === 'leadCategory' ? 'leadCategoryCode' : activeTab === 'activityStatus' ? 'activityStatusCode' : activeTab === 'leadStatus' ? 'leadStatusCode' : activeTab === 'leadVideoStatus' ? 'leadVideoStatusCode' : activeTab === 'leadOrderStatus' ? 'leadOrderStatusCode' : activeTab === 'leadCallStatus' ? 'leadCallStatusCode' : activeTab === 'experience' ? 'experienceCode' : activeTab === 'salaryComponent' ? 'salaryComponentCode' : activeTab === 'skill' ? 'skillCode' : activeTab === 'requirement' ? 'requirementCode' : 'id'] || item._id || item.id),
                "Name": item.name,
                ...(activeTab === 'designation' && { "Order": item.order }),
                "Status": item.isActive ? 'Active' : 'Inactive',
                "Created At": createDate ? formatDate(createDate) : '-',
                "Updated At": updateDate ? formatDate(updateDate) : '-'
            };

            if (['designation', 'courseType', 'documentType', 'regularizationReason', 'leaveType', 'holidayType', 'policyCategory', 'source', 'leadSource', 'leadDocumentType', 'leadCategory', 'activityStatus', 'leadStatus', 'leadVideoStatus', 'leadOrderStatus', 'leadCallStatus', 'industry', 'decisionMaker', 'experience', 'salaryComponent', 'skill', 'requirement', 'learningPathCategory', 'candidateAvailability'].includes(activeTab)) {
                base["Description"] = item.description || '-';
            }
            return base;
        });
    };

    const handleExportClick = () => {
        if (selectedRows.size > 0) {
            const sourceData = getCurrentTabData();
            const selectedData = sourceData.filter(item => selectedRows.has(item._id || item.id));
            const exportData = mapDataForExport(selectedData);

            const title = activeTab.charAt(0).toUpperCase() + activeTab.slice(1).replace(/([A-Z])/g, ' $1').trim();
            exportToCSV(exportData, `${activeTab}_export`, {
                title: `${title} List`,
                type: 'Selected Items'
            });
            setSelectedRows(new Set());
        } else {
            setShowExportModal(true);
        }
    };

    const handleExportCurrentPage = () => {
        const sourceData = getCurrentTabData();
        const exportData = mapDataForExport(sourceData);
        const title = activeTab.charAt(0).toUpperCase() + activeTab.slice(1).replace(/([A-Z])/g, ' $1').trim();

        exportToCSV(exportData, `${activeTab}_export`, {
            title: `${title} List`,
            type: 'Current Page'
        });
        setShowExportModal(false);
    };

    // Helper to fetch all data for export
    const fetchAllForExport = async (filters = {}) => {
        let service;
        switch (activeTab) {
            case 'industry': service = industryService; break;
            case 'decisionMaker': service = decisionMakerService; break;
            case 'designation': service = designationService; break;
            case 'courseType': service = courseTypeService; break;
            case 'documentType': service = documentTypeService; break;
            case 'regularizationReason': service = regularizationReasonService; break;
            case 'leaveType': service = leaveTypeService; break;
            case 'holidayType': service = holidayTypeService; break;
            case 'policyCategory': service = policyCategoryService; break;
            case 'source': service = sourceService; break;
            case 'leadSource': service = leadSourceService; break;
            case 'leadDocumentType': service = leadDocumentTypeService; break;
            case 'leadCategory': service = leadCategoryService; break;
            case 'activityStatus': service = activityStatusService; break;
            case 'leadStatus': service = leadStatusService; break;
            case 'leadVideoStatus': service = leadVideoStatusService; break;
            case 'leadOrderStatus': service = leadOrderStatusService; break;
            case 'leadCallStatus': service = leadCallStatusService; break;
            case 'experience': service = experienceService; break;
            case 'salaryComponent': service = salaryComponentService; break;
            case 'skill': service = skillService; break;
            case 'requirement': service = requirementService; break;
            case 'learningPathCategory': service = learningPathCategoryService; break;
            case 'internalTicketType': service = internalTicketTypeService; break;
            case 'internalApprovalCategory': service = internalApprovalCategoryService; break;
            default: return { data: [] };
        }

        // Pass search and status filters to API
        return await service.getAll({
            search: searchTerm,
            ...(statusFilter !== 'all' && { isActive: statusFilter === 'active' }),
            limit: 1000, // Reasonable limit for "All" export
            page: 1,
            ...filters
        });
    };

    const handleExportAll = async () => {
        setExportLoading(true);
        try {
            const response = await fetchAllForExport();
            const data = response.data || [];

            if (data.length > 0) {
                const exportData = mapDataForExport(data);
                const title = activeTab.charAt(0).toUpperCase() + activeTab.slice(1).replace(/([A-Z])/g, ' $1').trim();

                exportToCSV(exportData, `${activeTab}_export`, {
                    title: `${title} List`,
                    type: 'All Data'
                });
                setShowExportModal(false);
            } else {
                toast.error('No data available to export');
            }
        } catch (error) {
            toast.error('Failed to export data');
        } finally {
            setExportLoading(false);
        }
    };

    const handleExportRange = async (startDate, endDate) => {
        setExportLoading(true);
        try {
            // Fetch all data matching current filters first
            const response = await fetchAllForExport();
            const sourceData = response.data || [];

            // Robustly parse "YYYY-MM-DD" to local start/end of day
            const [startYear, startMonth, startDay] = startDate.split('-').map(Number);
            const start = new Date(startYear, startMonth - 1, startDay, 0, 0, 0, 0);

            const [endYear, endMonth, endDay] = endDate.split('-').map(Number);
            const end = new Date(endYear, endMonth - 1, endDay, 23, 59, 59, 999);

            const filteredData = sourceData.filter(item => {
                const itemDate = getItemDate(item);
                if (!itemDate) return false;
                // Compare timestamps directly against the full day range
                return itemDate.getTime() >= start.getTime() && itemDate.getTime() <= end.getTime();
            });

            if (filteredData.length > 0) {
                const exportData = mapDataForExport(filteredData);
                const title = activeTab.charAt(0).toUpperCase() + activeTab.slice(1).replace(/([A-Z])/g, ' $1').trim();

                exportToCSV(exportData, `${activeTab}_export`, {
                    title: `${title} List`,
                    type: 'Date Range',
                    dateRange: { startDate, endDate }
                });
                setShowExportModal(false);
            } else {
                toast.error('No data found for the selected date range');
            }
        } catch (error) {
            toast.error('Failed to export data');
        } finally {
            setExportLoading(false);
        }
    };

    const handleExportFiltered = async () => {
        setExportLoading(true);
        try {
            const response = await fetchAllForExport({
                startDate: dateRange.startDate || undefined,
                endDate: dateRange.endDate || undefined
            });
            const data = response.data || [];

            if (data.length > 0) {
                const exportData = mapDataForExport(data);
                const title = activeTab.charAt(0).toUpperCase() + activeTab.slice(1).replace(/([A-Z])/g, ' $1').trim();

                exportToCSV(exportData, `${activeTab}_export`, {
                    title: `${title} List`,
                    type: 'Filtered Data'
                });
                setShowExportModal(false);
            } else {
                toast.error('No filtered data available to export');
            }
        } catch (error) {
            toast.error('Failed to export data');
        } finally {
            setExportLoading(false);
        }
    };

    const renderTable = (data, type, loading) => {
        if (loading) {
            return (
                <div className="flex items-center justify-center py-12">
                    <RefreshCw className="h-8 w-8 text-gray-400 animate-spin" />
                    <span className="ml-3 text-gray-600">Loading...</span>
                </div>
            );
        }

        // Show no results found when search is active but no data
        if (data.length === 0 && searchTerm.trim()) {
            const itemName = type === 'industry' ? 'industries' :
                type === 'decisionMaker' ? 'decision makers' :
                    type === 'designation' ? 'designations' :
                    type === 'courseType' ? 'course types' :
                        type === 'documentType' ? 'document types' :
                            type === 'leaveType' ? 'leave types' :
                                type === 'holidayType' ? 'holiday types' :
                                    type === 'policyCategory' ? 'policy categories' :
                                        type === 'source' ? 'sources' :
                                            type === 'leadSource' ? 'lead sources' :
                                                type === 'leadDocumentType' ? 'lead document types' :
                                                    type === 'leadCategory' ? 'lead categories' :
                                                        type === 'leadStatus' ? 'lead statuses' :
                                                        type === 'leadVideoStatus' ? 'lead video statuses' : type === 'leadOrderStatus' ? 'lead order statuses' : type === 'leadCallStatus' ? 'lead call statuses' :
                                                        type === 'experience' ? 'job experience levels' :
                                                            type === 'skill' ? 'skills' :
                                                                type === 'requirement' ? 'requirements' :
                                                                    type === 'learningPathCategory' ? 'learning path categories' :
                                                                        type === 'candidateAvailability' ? 'candidate availabilities' :
                                                                            'regularization reasons';
            return (
                <div className="text-center py-12">
                    <SearchX className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Results Found</h3>
                    <p className="text-gray-600 mb-4">
                        No {itemName} found matching "{searchTerm}"
                    </p>
                    <Button
                        variant="outline"
                        onClick={handleClearSearch}
                        className="text-gray-600"
                    >
                        Clear Search
                    </Button>
                </div>
            );
        }

        // Show empty state when no data and no search
        if (data.length === 0) {
            let itemName = '';
            let itemNameSingular = '';
            if (type === 'industry') { itemName = 'Industries'; itemNameSingular = 'industry'; }
            else if (type === 'decisionMaker') { itemName = 'Decision Makers'; itemNameSingular = 'decision maker'; }
            else if (type === 'designation') { itemName = 'Designations'; itemNameSingular = 'designation'; }
            else if (type === 'courseType') { itemName = 'Course Types'; itemNameSingular = 'course type'; }
            else if (type === 'documentType') { itemName = 'Document Types'; itemNameSingular = 'document type'; }
            else if (type === 'regularizationReason') { itemName = 'Regularization Reasons'; itemNameSingular = 'regularization reason'; }
            else if (type === 'leaveType') { itemName = 'Leave Types'; itemNameSingular = 'leave type'; }
            else if (type === 'holidayType') { itemName = 'Holiday Types'; itemNameSingular = 'holiday type'; }
            else if (type === 'policyCategory') { itemName = 'Policy Categories'; itemNameSingular = 'policy category'; }
            else if (type === 'source') { itemName = 'Sources'; itemNameSingular = 'source'; }
            else if (type === 'leadSource') { itemName = 'Lead Sources'; itemNameSingular = 'lead source'; }
            else if (type === 'leadDocumentType') { itemName = 'Lead Document Types'; itemNameSingular = 'lead document type'; }
            else if (type === 'leadCategory') { itemName = 'Lead Categories'; itemNameSingular = 'lead category'; }
            else if (type === 'activityStatus') { itemName = 'Activity Statuses'; itemNameSingular = 'activity status'; }
            else if (type === 'leadStatus') { itemName = 'Lead Statuses'; itemNameSingular = 'lead status'; }
            else if (type === 'leadVideoStatus') { itemName = 'Lead Video Statuses'; itemNameSingular = 'lead video status'; } else if (type === 'leadOrderStatus') { itemName = 'Lead Order Statuses'; itemNameSingular = 'lead order status'; } else if (type === 'leadCallStatus') { itemName = 'Lead Call Statuses'; itemNameSingular = 'lead call status'; }
            else if (type === 'experience') { itemName = 'Experiences'; itemNameSingular = 'experience'; }
            else if (type === 'skill') { itemName = 'Skills'; itemNameSingular = 'skill'; }
            else if (type === 'requirement') { itemName = 'Requirements'; itemNameSingular = 'requirement'; }
            else if (type === 'learningPathCategory') { itemName = 'Learning Path Categories'; itemNameSingular = 'learning path category'; }
            else if (type === 'candidateAvailability') { itemName = 'Candidate Availabilities'; itemNameSingular = 'candidate availability'; }

            return (
                <div className="text-center py-12">
                    <Database className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No {itemName} Found</h3>
                    <p className="text-gray-600 mb-4">Create your first {itemNameSingular} to get started.</p>
                    <Button
                        variant="primary"
                        icon={<Plus className="h-4 w-4" />}
                        onClick={() => openCreateModal(type)}
                        className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                    >
                        Add {itemNameSingular.charAt(0).toUpperCase() + itemNameSingular.slice(1)}
                    </Button>
                </div>
            );
        }

        // Sorting logic (Filtering is now handled by backend)
        const sortedData = [...data].sort((a, b) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];

            if (sortConfig.key === 'createdAt') {
                aValue = getItemDate(a)?.getTime() || 0;
                bValue = getItemDate(b)?.getTime() || 0;
            } else if (sortConfig.key === 'updatedAt') {
                aValue = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
                bValue = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
            } else if (typeof aValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = (bValue || '').toLowerCase();
            } else if (typeof aValue === 'boolean') {
                aValue = aValue ? 1 : 0;
                bValue = bValue ? 1 : 0;
            }

            if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });

        // Pagination calculations
        const totalItems = sortedData.length;
        const totalPages = Math.ceil(totalItems / pageLimit) || 1;

        let validCurrentPage = currentPage;
        if (validCurrentPage > totalPages) validCurrentPage = totalPages;
        if (validCurrentPage < 1) validCurrentPage = 1;

        const startIndex = (validCurrentPage - 1) * pageLimit;
        const paginatedData = sortedData.slice(startIndex, startIndex + pageLimit);

        const getCodeField = (itemType) => {
            switch (itemType) {
                case 'industry': return 'industryCode';
                case 'decisionMaker': return 'decisionMakerCode';
                case 'designation': return 'designationCode';
                case 'courseType': return 'courseTypeId';
                case 'documentType': return 'documentTypeCode';
                case 'regularizationReason': return 'reasonCode';
                case 'leaveType': return 'leaveTypeCode';
                case 'holidayType': return 'holidayTypeCode';
                case 'policyCategory': return 'code';
                case 'source': return 'sourceCode';
                case 'leadSource': return 'leadSourceCode';
                case 'leadDocumentType': return 'leadDocumentTypeCode';
                case 'leadCategory': return 'leadCategoryCode';
                case 'activityStatus': return 'activityStatusCode';
                case 'leadStatus': return 'leadStatusCode';
                case 'leadVideoStatus': return 'leadVideoStatusCode';
                case 'leadOrderStatus': return 'leadOrderStatusCode';
                case 'leadCallStatus': return 'leadCallStatusCode';
                case 'experience': return 'experienceCode';
                case 'salaryComponent': return 'salaryComponentCode';
                case 'skill': return 'skillCode';
                case 'requirement': return 'requirementCode';
                case 'learningPathCategory': return 'categoryCode';
                case 'candidateAvailability': return 'availabilityCode';
                case 'internalTicketType': return 'ticketTypeCode';
                case 'internalApprovalCategory': return 'categoryCode';
                default: return 'id';
            }
        };

        const getCodeLabel = (itemType) => {
            switch (itemType) {
                case 'industry': return 'Industry Code';
                case 'decisionMaker': return 'Decision Maker Code';
                case 'designation': return 'Designation Code';
                case 'courseType': return 'Course Type ID';
                case 'documentType': return 'Document Type Code';
                case 'regularizationReason': return 'Reason Code';
                case 'leaveType': return 'Leave Type Code';
                case 'holidayType': return 'Holiday Type Code';
                case 'policyCategory': return 'Category Code';
                case 'source': return 'Source Code';
                case 'leadSource': return 'Lead Source Code';
                case 'leadDocumentType': return 'Lead Document Type Code';
                case 'leadCategory': return 'Lead Category Code';
                case 'activityStatus': return 'Activity Status Code';
                case 'leadStatus': return 'Value Key';
                case 'leadVideoStatus': return 'Lead Video Status Code';
                case 'leadOrderStatus': return 'Lead Order Status Code';
                case 'leadCallStatus': return 'Lead Call Status Code';
                case 'experience': return 'Job Experience Code';
                case 'salaryComponent': return 'Component Code';
                case 'skill': return 'Skill Code';
                case 'requirement': return 'Requirement Code';
                case 'learningPathCategory': return 'Category Code';
                case 'candidateAvailability': return 'Availability Code';
                case 'internalTicketType': return 'Ticket Type Code';
                case 'internalApprovalCategory': return 'Category Code';
                default: return 'ID';
            }
        };

        const getNameLabel = (itemType) => {
            switch (itemType) {
                case 'industry': return 'Industry Name';
                case 'decisionMaker': return 'Decision Maker Name';
                case 'designation': return 'Designation Name';
                case 'courseType': return 'Name';
                case 'documentType': return 'Name';
                case 'regularizationReason': return 'Reason Name';
                case 'leaveType': return 'Leave Type';
                case 'holidayType': return 'Holiday Type';
                case 'learningPathCategory': return 'Category Name';
                case 'candidateAvailability': return 'Availability Name';
                case 'policyCategory': return 'Category Name';
                case 'source': return 'Source Name';
                case 'leadSource': return 'Lead Source Name';
                case 'leadDocumentType': return 'Lead Document Type Name';
                case 'leadCategory': return 'Lead Category Name';
                case 'activityStatus': return 'Activity Status Name';
                case 'leadStatus': return 'Lead Status Name';
                case 'leadVideoStatus': return 'Lead Video Status Name';
                case 'leadOrderStatus': return 'Lead Order Status Name';
                case 'leadCallStatus': return 'Lead Call Status Name';
                case 'experience': return 'Job Experience Name';
                case 'salaryComponent': return 'Component Name';
                case 'skill': return 'Skill Name';
                case 'requirement': return 'Requirement Name';
                case 'internalTicketType': return 'Ticket Type Name';
                case 'internalApprovalCategory': return 'Category Name';
                default: return 'Name';
            }
        };

        return (
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-4">
                                <input
                                    type="checkbox"
                                    className="h-4 w-4 text-agrosaf-green focus:ring-agrosaf-green border-gray-300 rounded"
                                    checked={paginatedData.length > 0 && paginatedData.every(item => selectedRows.has(item._id || item.id))}
                                    onChange={handleSelectAll}
                                />
                            </th>
                            <th
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors group"
                                onClick={() => handleSort(getCodeField(type))}
                            >
                                <div className="flex items-center space-x-1">
                                    <span>{getCodeLabel(type)}</span>
                                    {getSortIndicator(getCodeField(type))}
                                </div>
                            </th>
                            <th
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors group"
                                onClick={() => handleSort('name')}
                            >
                                <div className="flex items-center space-x-1">
                                    <span>{getNameLabel(type)}</span>
                                    {getSortIndicator('name')}
                                </div>
                            </th>
                            {type === 'designation' && (
                                <th
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors group"
                                    onClick={() => handleSort('order')}
                                >
                                    <div className="flex items-center space-x-1">
                                        <span>Order</span>
                                        {getSortIndicator('order')}
                                    </div>
                                </th>
                            )}
                            {(type === 'designation' || type === 'courseType' || type === 'documentType' || type === 'regularizationReason' || type === 'leaveType' || type === 'holidayType' || type === 'policyCategory' || type === 'source' || type === 'leadSource' || type === 'leadDocumentType' || type === 'leadCategory' || type === 'activityStatus' || type === 'leadStatus' || type === 'leadVideoStatus' || type === 'industry' || type === 'decisionMaker' || type === 'experience' || type === 'salaryComponent' || type === 'skill' || type === 'requirement' || type === 'learningPathCategory' || type === 'candidateAvailability' || type === 'internalApprovalCategory') && (
                                <th
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors group"
                                    onClick={() => handleSort('description')}
                                >
                                    <div className="flex items-center space-x-1">
                                        <span>Description</span>
                                        {getSortIndicator('description')}
                                    </div>
                                </th>
                            )}
                            <th
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors group"
                                onClick={() => handleSort('isActive')}
                            >
                                <div className="flex items-center space-x-1">
                                    <span>Status</span>
                                    {getSortIndicator('isActive')}
                                </div>
                            </th>
                            <th
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors group"
                                onClick={() => handleSort('createdAt')}
                            >
                                <div className="flex items-center space-x-1">
                                    <span>Created At</span>
                                    {getSortIndicator('createdAt')}
                                </div>
                            </th>
                            <th
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors group"
                                onClick={() => handleSort('updatedAt')}
                            >
                                <div className="flex items-center space-x-1">
                                    <span>Updated At</span>
                                    {getSortIndicator('updatedAt')}
                                </div>
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {paginatedData.map((item) => (
                            <tr key={item._id || item.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <input
                                        type="checkbox"
                                        className="h-4 w-4 text-agrosaf-green focus:ring-agrosaf-green border-gray-300 rounded"
                                        checked={selectedRows.has(item._id || item.id)}
                                        onChange={() => handleSelectRow(item._id || item.id)}
                                    />
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-mono text-gray-600 bg-gray-100 px-2 py-1 rounded inline-block">
                                        {type === 'leadStatus' ? (item.value || '-') : (item[getCodeField(type)] || '-')}
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900 max-w-[200px] truncate capitalize" title={item.name}>{item.name}</div>
                                </td>
                                {type === 'designation' && (
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">{item.order ?? 1}</div>
                                    </td>
                                )}
                                {(type === 'designation' || type === 'courseType' || type === 'documentType' || type === 'regularizationReason' || type === 'leaveType' || type === 'holidayType' || type === 'policyCategory' || type === 'source' || type === 'leadSource' || type === 'leadDocumentType' || type === 'leadCategory' || type === 'activityStatus' || type === 'leadStatus' || type === 'leadVideoStatus' || type === 'industry' || type === 'decisionMaker' || type === 'experience' || type === 'salaryComponent' || type === 'skill' || type === 'requirement' || type === 'learningPathCategory' || type === 'candidateAvailability' || type === 'internalApprovalCategory') && (
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-500 max-w-[200px] truncate capitalize" title={item.description || '-'}>
                                            {item.description || '-'}
                                        </div>
                                    </td>
                                )}
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${item.isActive
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-gray-100 text-gray-800'
                                        }`}>
                                        {item.isActive ? 'Active' : 'Inactive'}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900">
                                        {formatDate(getItemDate(item)) || '-'}
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900">
                                        {item.updatedAt ? formatDate(new Date(item.updatedAt)) : '-'}
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                    <div className="flex items-center space-x-3">
                                        <button
                                            onClick={() => openViewModal(item, type)}
                                            className="text-gray-600 hover:text-gray-900"
                                            title="View Details"
                                        >
                                            <Eye className="h-4 w-4" />
                                        </button>
                                        <button
                                            onClick={() => openEditModal(item, type)}
                                            className="text-blue-600 hover:text-blue-900"
                                            title={item.isUndeletable ? 'Edit Name & Description' : 'Edit'}
                                        >
                                            <Edit className="h-4 w-4" />
                                        </button>
                                        {!item.isUndeletable && (
                                            <>
                                                <button
                                                    onClick={() => handleToggleStatus(item, type)}
                                                    className={item.isActive ? 'text-orange-500 hover:text-orange-700' : 'text-green-600 hover:text-green-800'}
                                                    title={item.isActive ? 'Deactivate' : 'Activate'}
                                                >
                                                    {item.isActive ? <ToggleRight className="h-5 w-5" /> : <ToggleLeft className="h-5 w-5" />}
                                                </button>
                                                <button
                                                    onClick={() => openDeleteModal(item, type)}
                                                    className="text-red-600 hover:text-red-900"
                                                    title="Delete"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </>
                                        )}
                                        {item.isUndeletable && (
                                            <span className="text-gray-400 text-xs italic">System Required</span>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table >
                {totalItems > 10 && (
                    <div className="mt-4 mb-4 px-6">
                        <PaginationControls
                            currentPage={validCurrentPage}
                            totalPages={totalPages}
                            totalItems={totalItems}
                            limit={pageLimit}
                            hasNextPage={validCurrentPage < totalPages}
                            hasPrevPage={validCurrentPage > 1}
                            onPageChange={(page) => {
                                setCurrentPage(page);
                            }}
                            onLimitChange={(newLimit) => {
                                setPageLimit(newLimit);
                                setCurrentPage(1);
                            }}
                            limitOptions={[10, 25, 50, 100]}
                        />
                    </div>
                )}
            </div >
        );
    };

    const renderTicketSettings = () => {
        const handleSaveTicketSettings = async (e) => {
            e.preventDefault();
            setSettingsSaving(true);
            try {
                const response = await internalTicketService.updateSettings({
                    adminEmails: [ticketSettingsData.adminEmail, ticketSettingsData.fallbackEmail]
                });
                if (response.success) {
                    toast.success('Ticket settings updated successfully!');
                    refetchTicketSettings();
                } else {
                    toast.error(response.message || 'Failed to update settings');
                }
            } catch (err) {
                toast.error(err.message || 'Failed to update settings');
            } finally {
                setSettingsSaving(false);
            }
        };

        return (
            <div className="max-w-2xl bg-white rounded-xl border border-gray-150 shadow-sm p-6">
                <div className="flex items-center space-x-3 mb-6 pb-4 border-b border-gray-100">
                    <div className="p-2 bg-green-50 rounded-lg text-agrosaf-green">
                        <Mail className="h-6 w-6" />
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">Ticket Settings</h3>
                        <p className="text-sm text-gray-500">Configure administrative email notification rules for internal tickets.</p>
                    </div>
                </div>
                <form onSubmit={handleSaveTicketSettings} className="space-y-6">
                    <div className="grid grid-cols-1 gap-6">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Administrative Notification Email <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="email"
                                required
                                value={ticketSettingsData.adminEmail}
                                onChange={(e) => setTicketSettingsData({ ...ticketSettingsData, adminEmail: e.target.value })}
                                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent text-sm transition-all"
                                placeholder="e.g. support-admin@company.com"
                            />
                            <p className="mt-1.5 text-xs text-gray-500">
                                This email will receive notifications for all ticket updates and assignments.
                            </p>
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Fallback Notification Email <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="email"
                                required
                                value={ticketSettingsData.fallbackEmail}
                                onChange={(e) => setTicketSettingsData({ ...ticketSettingsData, fallbackEmail: e.target.value })}
                                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent text-sm transition-all"
                                placeholder="e.g. backup-admin@company.com"
                            />
                            <p className="mt-1.5 text-xs text-gray-500">
                                Used when primary notification delivery fails or when specific assignments are not mapped.
                            </p>
                        </div>
                    </div>
                    <div className="flex justify-end pt-4 border-t border-gray-100">
                        <Button
                            type="submit"
                            variant="primary"
                            className="bg-agrosaf-green hover:bg-agrosaf-darkGreen text-white px-5 py-2"
                        >
                            Save Settings
                        </Button>
                    </div>
                </form>
            </div>
        );
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">
                        {module === 'crm' ? 'CRM Configuration' : 'HRMS Configuration'}
                    </h1>
                    <p className="text-gray-600">
                        {module === 'crm'
                            ? 'Manage Industry, Decision Maker, Lead Source, Lead Document Type, Lead Category, and Lead Status master data'
                            : 'Manage Designation, Course Type, Document Type, Leave Type, Holiday Type, Policy Category, Job Source, Job Experience, Job Skill, Job Requirement, and Regularization Reason master data'
                        }
                    </p>
                </div>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="border-b border-gray-200">
                    <nav className="-mb-px flex flex-wrap gap-x-8 gap-y-2 px-6">
                        {/* CRM tabs */}
                        {allowedTabs.includes('industry') && (
                            <button
                                onClick={() => { setActiveTab('industry'); setSearchTerm(''); setStatusFilter('all'); setDateRange({ startDate: '', endDate: '' }); }}
                                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'industry'
                                    ? 'border-agrosaf-green text-agrosaf-green'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <div className="flex items-center space-x-2">
                                    <Building2 className="h-4 w-4" />
                                    <span>Industry</span>
                                </div>
                            </button>
                        )}
                        {allowedTabs.includes('decisionMaker') && (
                            <button
                                onClick={() => { setActiveTab('decisionMaker'); setSearchTerm(''); setStatusFilter('all'); setDateRange({ startDate: '', endDate: '' }); }}
                                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'decisionMaker'
                                    ? 'border-agrosaf-green text-agrosaf-green'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <div className="flex items-center space-x-2">
                                    <Users className="h-4 w-4" />
                                    <span>Decision Maker</span>
                                </div>
                            </button>
                        )}
                        {/* HRMS tabs */}
                        {allowedTabs.includes('courseType') && (
                            <button
                                onClick={() => { setActiveTab('courseType'); setSearchTerm(''); setStatusFilter('all'); setDateRange({ startDate: '', endDate: '' }); }}
                                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'courseType'
                                    ? 'border-agrosaf-green text-agrosaf-green'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <div className="flex items-center space-x-2">
                                    <GraduationCap className="h-4 w-4" />
                                    <span>Course Type</span>
                                </div>
                            </button>
                        )}
                        {allowedTabs.includes('documentType') && (
                            <button
                                onClick={() => { setActiveTab('documentType'); setSearchTerm(''); setStatusFilter('all'); setDateRange({ startDate: '', endDate: '' }); }}
                                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'documentType'
                                    ? 'border-agrosaf-green text-agrosaf-green'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <div className="flex items-center space-x-2">
                                    <FileText className="h-4 w-4" />
                                    <span>Document Type</span>
                                </div>
                            </button>
                        )}
                        {allowedTabs.includes('regularizationReason') && (
                            <button
                                onClick={() => { setActiveTab('regularizationReason'); setSearchTerm(''); setStatusFilter('all'); setDateRange({ startDate: '', endDate: '' }); }}
                                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'regularizationReason'
                                    ? 'border-agrosaf-green text-agrosaf-green'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <div className="flex items-center space-x-2">
                                    <RefreshCw className="h-4 w-4" />
                                    <span>Regularization Reason</span>
                                </div>
                            </button>
                        )}
                        {allowedTabs.includes('leaveType') && (
                            <button
                                onClick={() => { setActiveTab('leaveType'); setSearchTerm(''); setStatusFilter('all'); setDateRange({ startDate: '', endDate: '' }); }}
                                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'leaveType'
                                    ? 'border-agrosaf-green text-agrosaf-green'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <div className="flex items-center space-x-2">
                                    <Building2 className="h-4 w-4" />
                                    <span>Leave Type</span>
                                </div>
                            </button>
                        )}
                        {allowedTabs.includes('holidayType') && (
                            <button
                                onClick={() => { setActiveTab('holidayType'); setSearchTerm(''); setStatusFilter('all'); setDateRange({ startDate: '', endDate: '' }); }}
                                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'holidayType'
                                    ? 'border-agrosaf-green text-agrosaf-green'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <div className="flex items-center space-x-2">
                                    <Calendar className="h-4 w-4" />
                                    <span>Holiday Type</span>
                                </div>
                            </button>
                        )}
                        {allowedTabs.includes('policyCategory') && (
                            <button
                                onClick={() => { setActiveTab('policyCategory'); setSearchTerm(''); setStatusFilter('all'); setDateRange({ startDate: '', endDate: '' }); }}
                                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'policyCategory'
                                    ? 'border-agrosaf-green text-agrosaf-green'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <div className="flex items-center space-x-2">
                                    <FileText className="h-4 w-4" />
                                    <span>Policy Category</span>
                                </div>
                            </button>
                        )}
                        {allowedTabs.includes('salaryComponent') && (
                            <button
                                onClick={() => { setActiveTab('salaryComponent'); setSearchTerm(''); setStatusFilter('all'); setDateRange({ startDate: '', endDate: '' }); }}
                                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'salaryComponent'
                                    ? 'border-agrosaf-green text-agrosaf-green'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <div className="flex items-center space-x-2">
                                    <Database className="h-4 w-4" />
                                    <span>Salary Component</span>
                                </div>
                            </button>
                        )}
                        {allowedTabs.includes('source') && (
                            <button
                                onClick={() => { setActiveTab('source'); setSearchTerm(''); setStatusFilter('all'); setDateRange({ startDate: '', endDate: '' }); }}
                                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'source'
                                    ? 'border-agrosaf-green text-agrosaf-green'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <div className="flex items-center space-x-2">
                                    <Database className="h-4 w-4" />
                                    <span>Job Source</span>
                                </div>
                            </button>
                        )}
                        {allowedTabs.includes('leadSource') && (
                            <button
                                onClick={() => { setActiveTab('leadSource'); setSearchTerm(''); setStatusFilter('all'); setDateRange({ startDate: '', endDate: '' }); }}
                                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'leadSource'
                                    ? 'border-agrosaf-green text-agrosaf-green'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <div className="flex items-center space-x-2">
                                    <Users className="h-4 w-4" />
                                    <span>Lead Source</span>
                                </div>
                            </button>
                        )}
                        {allowedTabs.includes('leadDocumentType') && (
                            <button
                                onClick={() => { setActiveTab('leadDocumentType'); setSearchTerm(''); setStatusFilter('all'); setDateRange({ startDate: '', endDate: '' }); }}
                                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'leadDocumentType'
                                    ? 'border-agrosaf-green text-agrosaf-green'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <div className="flex items-center space-x-2">
                                    <FileText className="h-4 w-4" />
                                    <span>Lead Document Type</span>
                                </div>
                            </button>
                        )}
                        {allowedTabs.includes('leadCategory') && (
                            <button
                                onClick={() => { setActiveTab('leadCategory'); setSearchTerm(''); setStatusFilter('all'); setDateRange({ startDate: '', endDate: '' }); }}
                                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'leadCategory'
                                    ? 'border-agrosaf-green text-agrosaf-green'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <div className="flex items-center space-x-2">
                                    <FileText className="h-4 w-4" />
                                    <span>Lead Category</span>
                                </div>
                            </button>
                        )}
                        {allowedTabs.includes('activityStatus') && (
                            <button
                                onClick={() => { setActiveTab('activityStatus'); setSearchTerm(''); setStatusFilter('all'); setDateRange({ startDate: '', endDate: '' }); }}
                                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'activityStatus'
                                    ? 'border-agrosaf-green text-agrosaf-green'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <div className="flex items-center space-x-2">
                                    <FileText className="h-4 w-4" />
                                    <span>Activity Status</span>
                                </div>
                            </button>
                        )}
                        {allowedTabs.includes('leadStatus') && (
                            <button
                                onClick={() => { setActiveTab('leadStatus'); setSearchTerm(''); setStatusFilter('all'); setDateRange({ startDate: '', endDate: '' }); }}
                                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'leadStatus'
                                    ? 'border-agrosaf-green text-agrosaf-green'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <div className="flex items-center space-x-2">
                                    <FileText className="h-4 w-4" />
                                    <span>Lead Status</span>
                                </div>
                            </button>
                        )}
                        {allowedTabs.includes('leadVideoStatus') && (
                            <button
                                onClick={() => { setActiveTab('leadVideoStatus'); setSearchTerm(''); setStatusFilter('all'); setDateRange({ startDate: '', endDate: '' }); }}
                                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'leadVideoStatus'
                                    ? 'border-agrosaf-green text-agrosaf-green'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <div className="flex items-center space-x-2">
                                    <FileText className="h-4 w-4" />
                                    <span>Lead Video Status</span>
                                </div>
                            </button>
                        )}
                        {allowedTabs.includes('leadOrderStatus') && (
                            <button
                                onClick={() => { setActiveTab('leadOrderStatus'); setSearchTerm(''); setStatusFilter('all'); setDateRange({ startDate: '', endDate: '' }); }}
                                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'leadOrderStatus'
                                    ? 'border-agrosaf-green text-agrosaf-green'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <div className="flex items-center space-x-2">
                                    <FileText className="h-4 w-4" />
                                    <span>Lead Order Status</span>
                                </div>
                            </button>
                        )}
                        {allowedTabs.includes('leadCallStatus') && (
                            <button
                                onClick={() => { setActiveTab('leadCallStatus'); setSearchTerm(''); setStatusFilter('all'); setDateRange({ startDate: '', endDate: '' }); }}
                                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'leadCallStatus'
                                    ? 'border-agrosaf-green text-agrosaf-green'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <div className="flex items-center space-x-2">
                                    <Phone className="h-4 w-4" />
                                    <span>Lead Call Status</span>
                                </div>
                            </button>
                        )}
                        {allowedTabs.includes('leadInterest') && (
                            <button
                                onClick={() => { setActiveTab('leadInterest'); setSearchTerm(''); setStatusFilter('all'); setDateRange({ startDate: '', endDate: '' }); }}
                                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'leadInterest'
                                    ? 'border-agrosaf-green text-agrosaf-green'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <div className="flex items-center space-x-2">
                                    <Heart className="h-4 w-4" />
                                    <span>Lead Interest</span>
                                </div>
                            </button>
                        )}
                        {allowedTabs.includes('companySize') && (
                            <button
                                onClick={() => { setActiveTab('companySize'); setSearchTerm(''); setStatusFilter('all'); setDateRange({ startDate: '', endDate: '' }); }}
                                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'companySize'
                                    ? 'border-agrosaf-green text-agrosaf-green'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <div className="flex items-center space-x-2">
                                    <Building2 className="h-4 w-4" />
                                    <span>Company Size</span>
                                </div>
                            </button>
                        )}
                        {allowedTabs.includes('internalTicketType') && (
                            <button
                                onClick={() => { setActiveTab('internalTicketType'); setSearchTerm(''); setStatusFilter('all'); setDateRange({ startDate: '', endDate: '' }); }}
                                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'internalTicketType'
                                    ? 'border-agrosaf-green text-agrosaf-green'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <div className="flex items-center space-x-2">
                                    <HelpCircle className="h-4 w-4" />
                                    <span>Ticket Type Master</span>
                                </div>
                            </button>
                        )}
                        {allowedTabs.includes('internalApprovalCategory') && (
                            <button
                                onClick={() => { setActiveTab('internalApprovalCategory'); setSearchTerm(''); setStatusFilter('all'); setDateRange({ startDate: '', endDate: '' }); }}
                                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'internalApprovalCategory'
                                    ? 'border-agrosaf-green text-agrosaf-green'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <div className="flex items-center space-x-2">
                                    <FileText className="h-4 w-4" />
                                    <span>Approval Category</span>
                                </div>
                            </button>
                        )}
                        {allowedTabs.includes('experience') && (
                            <button
                                onClick={() => { setActiveTab('experience'); setSearchTerm(''); setStatusFilter('all'); setDateRange({ startDate: '', endDate: '' }); }}
                                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'experience'
                                    ? 'border-agrosaf-green text-agrosaf-green'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <div className="flex items-center space-x-2">
                                    <Briefcase className="h-4 w-4" />
                                    <span>Job Experience</span>
                                </div>
                            </button>
                        )}
                        {allowedTabs.includes('skill') && (
                            <button
                                onClick={() => { setActiveTab('skill'); setSearchTerm(''); setStatusFilter('all'); setDateRange({ startDate: '', endDate: '' }); }}
                                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'skill'
                                    ? 'border-agrosaf-green text-agrosaf-green'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <div className="flex items-center space-x-2">
                                    <Briefcase className="h-4 w-4" />
                                    <span>Job Skills</span>
                                </div>
                            </button>
                        )}
                        {allowedTabs.includes('requirement') && (
                            <button
                                onClick={() => { setActiveTab('requirement'); setSearchTerm(''); setStatusFilter('all'); setDateRange({ startDate: '', endDate: '' }); }}
                                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'requirement'
                                    ? 'border-agrosaf-green text-agrosaf-green'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <div className="flex items-center space-x-2">
                                    <FileText className="h-4 w-4" />
                                    <span>Job Requirements</span>
                                </div>
                            </button>
                        )}
                        {allowedTabs.includes('learningPathCategory') && (
                            <button
                                onClick={() => { setActiveTab('learningPathCategory'); setSearchTerm(''); setStatusFilter('all'); setDateRange({ startDate: '', endDate: '' }); }}
                                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'learningPathCategory'
                                    ? 'border-agrosaf-green text-agrosaf-green'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <div className="flex items-center space-x-2">
                                    <FileText className="h-4 w-4" />
                                    <span>Learning Path Category</span>
                                </div>
                            </button>
                        )}
                        {allowedTabs.includes('candidateAvailability') && (
                            <button
                                onClick={() => { setActiveTab('candidateAvailability'); setSearchTerm(''); setStatusFilter('all'); setDateRange({ startDate: '', endDate: '' }); }}
                                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'candidateAvailability'
                                    ? 'border-agrosaf-green text-agrosaf-green'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <div className="flex items-center space-x-2">
                                    <Users className="h-4 w-4" />
                                    <span>Candidate Availability</span>
                                </div>
                            </button>
                        )}
                        {allowedTabs.includes('designation') && (
                            <button
                                onClick={() => { setActiveTab('designation'); setSearchTerm(''); setStatusFilter('all'); setDateRange({ startDate: '', endDate: '' }); }}
                                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'designation'
                                    ? 'border-agrosaf-green text-agrosaf-green'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <div className="flex items-center space-x-2">
                                    <Briefcase className="h-4 w-4" />
                                    <span>Designation</span>
                                </div>
                            </button>
                        )}
                    </nav>

                </div>

                {/* Tab Content */}
                <div className="p-6">
                    {/* Search and Add Button */}
                    {activeTab !== 'leadInterest' && activeTab !== 'companySize' && activeTab !== 'ticketSettings' && (
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                            <div className="flex-1 w-full flex flex-col sm:flex-row gap-4">
                                <div className="relative flex-1">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder={`Search ${activeTab === 'industry' ? 'industries' : activeTab === 'decisionMaker' ? 'decision makers' : activeTab === 'designation' ? 'designations' : activeTab === 'courseType' ? 'course types' : activeTab === 'documentType' ? 'document types' : activeTab === 'leaveType' ? 'leave types' : activeTab === 'holidayType' ? 'holiday types' : activeTab === 'policyCategory' ? 'policy categories' : activeTab === 'salaryComponent' ? 'salary components' : activeTab === 'source' ? 'sources' : activeTab === 'leadSource' ? 'lead sources' : activeTab === 'leadDocumentType' ? 'lead document types' : activeTab === 'leadCategory' ? 'lead categories' : activeTab === 'activityStatus' ? 'activity statuses' : activeTab === 'leadStatus' ? 'lead statuses' : activeTab === 'leadVideoStatus' ? 'lead video statuses' : activeTab === 'leadOrderStatus' ? 'lead order statuses' : activeTab === 'leadCallStatus' ? 'lead call statuses' : activeTab === 'experience' ? 'job experience levels' : activeTab === 'skill' ? 'job skills' : activeTab === 'requirement' ? 'job requirements' : activeTab === 'learningPathCategory' ? 'learning path categories' : activeTab === 'candidateAvailability' ? 'candidate availabilities' : activeTab === 'internalTicketType' ? 'ticket types' : 'regularization reasons'}...`}
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent w-full"
                                    />
                                    {searchTerm && (
                                        <button
                                            onClick={handleClearSearch}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        >
                                            <X className="h-4 w-4" />
                                        </button>
                                    )}
                                </div>
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent min-w-[150px]"
                                >
                                    <option value="all">All Status</option>
                                    <option value="active">Active Only</option>
                                    <option value="inactive">Inactive Only</option>
                                </select>
                                <DateRangeFilter
                                    startDate={dateRange.startDate}
                                    endDate={dateRange.endDate}
                                    onChange={setDateRange}
                                />
                            </div>
                            <div className="flex items-center gap-2">
                                {selectedRows.size > 0 && (
                                    <Button
                                        variant="danger"
                                        icon={<Trash2 className="h-4 w-4" />}
                                        onClick={() => setShowBulkDeleteModal(true)}
                                        className="bg-red-600 hover:bg-red-700 text-white"
                                    >
                                        Delete ({selectedRows.size})
                                    </Button>
                                )}
                                <Button
                                    variant="outline"
                                    icon={<Download className="h-4 w-4" />}
                                    onClick={handleExportClick}
                                    disabled={exportLoading}
                                >
                                    {selectedRows.size > 0 ? `Export (${selectedRows.size})` : 'Export'}
                                </Button>
                                <Button
                                    variant="primary"
                                    icon={<Plus className="h-4 w-4" />}
                                    onClick={() => openCreateModal(activeTab)}
                                    className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                                >
                                    Add {activeTab === 'industry' ? 'Industry' : activeTab === 'decisionMaker' ? 'Decision Maker' : activeTab === 'designation' ? 'Designation' : activeTab === 'courseType' ? 'Course Type' : activeTab === 'documentType' ? 'Document Type' : activeTab === 'leaveType' ? 'Leave Type' : activeTab === 'holidayType' ? 'Holiday Type' : activeTab === 'policyCategory' ? 'Policy Category' : activeTab === 'salaryComponent' ? 'Salary Component' : activeTab === 'source' ? 'Source' : activeTab === 'leadSource' ? 'Lead Source' : activeTab === 'leadDocumentType' ? 'Lead Document Type' : activeTab === 'leadCategory' ? 'Lead Category' : activeTab === 'activityStatus' ? 'Activity Status' : activeTab === 'leadStatus' ? 'Lead Status' : activeTab === 'leadVideoStatus' ? 'Lead Video Status' : activeTab === 'leadOrderStatus' ? 'Lead Order Status' : activeTab === 'leadCallStatus' ? 'Lead Call Status' : activeTab === 'skill' ? 'Skill' : activeTab === 'requirement' ? 'Requirement' : activeTab === 'experience' ? 'Job Experience' : activeTab === 'learningPathCategory' ? 'Learning Path Category' : activeTab === 'candidateAvailability' ? 'Candidate Availability' : activeTab === 'internalTicketType' ? 'Ticket Type' : activeTab === 'internalApprovalCategory' ? 'Approval Category' : 'Regularization Reason'}
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Table */}
                    {activeTab === 'industry' && renderTable(industries, 'industry', loadingIndustries)}
                    {activeTab === 'decisionMaker' && renderTable(decisionMakers, 'decisionMaker', loadingDecisionMakers)}
                    {activeTab === 'designation' && renderTable(designations, 'designation', loadingDesignations)}
                    {activeTab === 'courseType' && renderTable(courseTypes, 'courseType', loadingCourseTypes)}
                    {activeTab === 'documentType' && renderTable(documentTypes, 'documentType', loadingDocumentTypes)}
                    {activeTab === 'regularizationReason' && renderTable(regularizationReasons, 'regularizationReason', loadingRegularizationReasons)}
                    {activeTab === 'leaveType' && renderTable(leaveTypes, 'leaveType', loadingLeaveTypes)}
                    {activeTab === 'holidayType' && renderTable(holidayTypes, 'holidayType', loadingHolidayTypes)}
                    {activeTab === 'policyCategory' && renderTable(policyCategories, 'policyCategory', loadingPolicyCategories)}
                    {activeTab === 'source' && renderTable(sources, 'source', loadingSources)}
                    {activeTab === 'leadSource' && renderTable(leadSources, 'leadSource', loadingLeadSources)}
                    {activeTab === 'leadDocumentType' && renderTable(leadDocumentTypes, 'leadDocumentType', loadingLeadDocumentTypes)}
                    {activeTab === 'leadCategory' && renderTable(leadCategories, 'leadCategory', loadingLeadCategories)}
                    {activeTab === 'activityStatus' && renderTable(activityStatuses, 'activityStatus', loadingActivityStatuses)}
                    {activeTab === 'leadStatus' && renderTable(leadStatuses, 'leadStatus', loadingLeadStatuses)}
                    {activeTab === 'leadVideoStatus' && renderTable(leadVideoStatuses, 'leadVideoStatus', loadingLeadVideoStatuses)}
                    {activeTab === 'leadOrderStatus' && renderTable(leadOrderStatuses, 'leadOrderStatus', loadingLeadOrderStatuses)}
                    {activeTab === 'leadCallStatus' && renderTable(leadCallStatuses, 'leadCallStatus', loadingLeadCallStatuses)}
                    {activeTab === 'leadInterest' && <LeadInterestManagement />}
                    {activeTab === 'companySize' && <CompanySizeManagement />}
                    {activeTab === 'experience' && renderTable(experiences, 'experience', loadingExperiences)}
                    {activeTab === 'salaryComponent' && renderTable(salaryComponents, 'salaryComponent', loadingSalaryComponents)}
                    {activeTab === 'skill' && renderTable(skills, 'skill', loadingSkills)}
                    {activeTab === 'requirement' && renderTable(requirements, 'requirement', loadingRequirements)}
                    {activeTab === 'learningPathCategory' && renderTable(learningPathCategories, 'learningPathCategory', loadingLearningPathCategories)}
                    {activeTab === 'candidateAvailability' && renderTable(candidateAvailabilities, 'candidateAvailability', loadingCandidateAvailabilities)}
                    {activeTab === 'internalTicketType' && renderTable(internalTicketTypes, 'internalTicketType', loadingInternalTicketTypes)}
                    {activeTab === 'internalApprovalCategory' && renderTable(internalApprovalCategories, 'internalApprovalCategory', loadingInternalApprovalCategories)}
                </div>
            </div>

            <ExportModal
                isOpen={showExportModal}
                onClose={() => setShowExportModal(false)}
                onExportCurrentPage={handleExportCurrentPage}
                onExportAll={handleExportAll}
                onExportRange={handleExportRange}
                onExportFiltered={handleExportFiltered}
                loading={exportLoading}
                title={`Export ${activeTab.charAt(0).toUpperCase() + activeTab.slice(1).replace(/([A-Z])/g, ' $1').trim()}`}
                entityName={activeTab.charAt(0).toUpperCase() + activeTab.slice(1).replace(/([A-Z])/g, ' $1').trim()}
            />

            {/* Create/Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4">
                        <div className="fixed inset-0 bg-black opacity-50" onClick={closeModal}></div>
                        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-900">
                                    {modalType === 'create' ? 'Add' : 'Edit'} {modalFor === 'industry' ? 'Industry' : modalFor === 'decisionMaker' ? 'Decision Maker' : modalFor === 'designation' ? 'Designation' : modalFor === 'courseType' ? 'Course Type' : modalFor === 'documentType' ? 'Document Type' : modalFor === 'leaveType' ? 'Leave Type' : modalFor === 'holidayType' ? 'Holiday Type' : modalFor === 'policyCategory' ? 'Policy Category' : modalFor === 'salaryComponent' ? 'Salary Component' : modalFor === 'source' ? 'Source' : modalFor === 'leadSource' ? 'Lead Source' : modalFor === 'leadDocumentType' ? 'Lead Document Type' : modalFor === 'leadCategory' ? 'Lead Category' : modalFor === 'activityStatus' ? 'Activity Status' : modalFor === 'leadStatus' ? 'Lead Status' : modalFor === 'leadVideoStatus' ? 'Lead Video Status' : modalFor === 'leadOrderStatus' ? 'Lead Order Status' : modalFor === 'leadCallStatus' ? 'Lead Call Status' : modalFor === 'skill' ? 'Skill' : modalFor === 'requirement' ? 'Requirement' : modalFor === 'experience' ? 'Job Experience' : modalFor === 'learningPathCategory' ? 'Learning Path Category' : modalFor === 'candidateAvailability' ? 'Candidate Availability' : modalFor === 'internalTicketType' ? 'Ticket Type' : modalFor === 'internalApprovalCategory' ? 'Approval Category' : 'Regularization Reason'}
                                </h3>
                                <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                                    <X className="h-5 w-5" />
                                </button>
                            </div>

                            <form onSubmit={handleFormSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            const limit = (modalFor === 'designation' || modalFor === 'documentType' || modalFor === 'leaveType' || modalFor === 'holidayType' || modalFor === 'policyCategory' || modalFor === 'source' || modalFor === 'leadSource' || modalFor === 'leadDocumentType' || modalFor === 'leadCategory' || modalFor === 'activityStatus' || modalFor === 'leadStatus' || modalFor === 'leadVideoStatus' || modalFor === 'leadOrderStatus' || modalFor === 'leadCallStatus' || modalFor === 'industry' || modalFor === 'decisionMaker' || modalFor === 'experience' || modalFor === 'salaryComponent' || modalFor === 'skill' || modalFor === 'requirement' || modalFor === 'learningPathCategory' || modalFor === 'candidateAvailability' || modalFor === 'internalTicketType' || modalFor === 'internalApprovalCategory') ? DOC_NAME_MAX_LENGTH : NAME_MAX_LENGTH;
                                            if (value.length <= limit) {
                                                setFormData({ ...formData, name: value });
                                                // Clear error on change if it exists and name is now valid enough to start typing
                                                if (formErrors.name) {
                                                    setFormErrors({});
                                                }
                                            }
                                        }}
                                        onBlur={() => handleBlur('name')}
                                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green ${formErrors.name ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'
                                            }`}
                                        placeholder={`Enter ${modalFor === 'industry' ? 'industry' : modalFor === 'decisionMaker' ? 'decision maker' : modalFor === 'designation' ? 'designation' : modalFor === 'courseType' ? 'course type' : modalFor === 'documentType' ? 'document type' : modalFor === 'leaveType' ? 'leave type' : modalFor === 'holidayType' ? 'holiday type' : modalFor === 'policyCategory' ? 'policy category' : modalFor === 'salaryComponent' ? 'salary component' : modalFor === 'source' ? 'source' : modalFor === 'leadSource' ? 'lead source' : modalFor === 'leadDocumentType' ? 'lead document type' : modalFor === 'leadCategory' ? 'lead category' : modalFor === 'activityStatus' ? 'activity status' : modalFor === 'leadStatus' ? 'lead status' : modalFor === 'leadVideoStatus' ? 'lead video status' : modalFor === 'leadOrderStatus' ? 'lead order status' : modalFor === 'leadCallStatus' ? 'lead call status' : modalFor === 'skill' ? 'skill' : modalFor === 'requirement' ? 'requirement' : modalFor === 'experience' ? 'job experience' : modalFor === 'learningPathCategory' ? 'learning path category' : modalFor === 'candidateAvailability' ? 'candidate availability' : modalFor === 'internalTicketType' ? 'ticket type' : modalFor === 'internalApprovalCategory' ? 'approval category' : 'regularization reason'} name`}
                                    />
                                    <div className="flex justify-between mt-1">
                                        <div>
                                            {formErrors.name && (
                                                <p className="text-red-500 text-sm">{formErrors.name}</p>
                                            )}
                                        </div>
                                        <p className={`text-xs ${(formData.name?.length || 0) >= ((modalFor === 'designation' || modalFor === 'documentType' || modalFor === 'leaveType' || modalFor === 'holidayType' || modalFor === 'policyCategory' || modalFor === 'source' || modalFor === 'leadSource' || modalFor === 'leadDocumentType' || modalFor === 'leadCategory' || modalFor === 'activityStatus' || modalFor === 'leadStatus' || modalFor === 'leadVideoStatus' || modalFor === 'leadOrderStatus' || modalFor === 'leadCallStatus' || modalFor === 'industry' || modalFor === 'decisionMaker' || modalFor === 'experience' || modalFor === 'salaryComponent' || modalFor === 'skill' || modalFor === 'requirement' || modalFor === 'learningPathCategory' || modalFor === 'candidateAvailability' || modalFor === 'internalTicketType' || modalFor === 'internalApprovalCategory') ? DOC_NAME_MAX_LENGTH : NAME_MAX_LENGTH) ? 'text-red-500' : 'text-gray-400'}`}>
                                            {getCharacterCount()}
                                        </p>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Min {NAME_MIN_LENGTH} characters. Only letters, numbers, spaces, hyphens, ampersands, plus signs, forward slashes, parentheses, commas, and dots allowed.
                                    </p>
                                </div>

                                {modalFor === 'experience' && (
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Min Year <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max="100"
                                                    value={formData.minYear}
                                                    onChange={(e) => {
                                                        const minVal = parseInt(e.target.value, 10) || 0;
                                                        const maxVal = formData.maxYear;
                                                        const newName = formData.noMax ? `${minVal}+ Years` : `${minVal}-${maxVal} Years`;
                                                        setFormData({ ...formData, minYear: minVal, name: newName });
                                                        if (formErrors.minYear || formErrors.maxYear) {
                                                            setFormErrors(prev => ({ ...prev, minYear: '', maxYear: '' }));
                                                        }
                                                    }}
                                                    onBlur={() => handleBlur('minYear')}
                                                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green ${formErrors.minYear ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'}`}
                                                    placeholder="Min"
                                                />
                                                {formErrors.minYear && (
                                                    <p className="text-red-500 text-xs mt-1">{formErrors.minYear}</p>
                                                )}
                                            </div>
                                            {!formData.noMax && (
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Max Year <span className="text-red-500">*</span>
                                                    </label>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max="100"
                                                        value={formData.maxYear}
                                                        onChange={(e) => {
                                                            const maxVal = parseInt(e.target.value, 10) || 0;
                                                            const minVal = formData.minYear;
                                                            const newName = `${minVal}-${maxVal} Years`;
                                                            setFormData({ ...formData, maxYear: maxVal, name: newName });
                                                            if (formErrors.minYear || formErrors.maxYear) {
                                                                setFormErrors(prev => ({ ...prev, minYear: '', maxYear: '' }));
                                                            }
                                                        }}
                                                        onBlur={() => handleBlur('maxYear')}
                                                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green ${formErrors.maxYear ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'}`}
                                                        placeholder="Max"
                                                    />
                                                    {formErrors.maxYear && (
                                                        <p className="text-red-500 text-xs mt-1">{formErrors.maxYear}</p>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex items-center">
                                            <input
                                                type="checkbox"
                                                id="noMaxLimit"
                                                checked={formData.noMax}
                                                onChange={(e) => {
                                                    const checked = e.target.checked;
                                                    const minVal = formData.minYear;
                                                    const maxVal = formData.maxYear;
                                                    const newName = checked ? `${minVal}+ Years` : `${minVal}-${maxVal} Years`;
                                                    setFormData({ ...formData, noMax: checked, name: newName });
                                                    setFormErrors(prev => ({ ...prev, minYear: '', maxYear: '' }));
                                                }}
                                                className="h-4 w-4 text-agrosaf-green border-gray-300 rounded focus:ring-agrosaf-green"
                                            />
                                            <label htmlFor="noMaxLimit" className="ml-2 block text-sm text-gray-700">
                                                No upper limit (e.g., 10+ Years)
                                            </label>
                                        </div>
                                    </div>
                                )}

                                {modalFor === 'designation' && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Order <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="number"
                                            min="1"
                                            value={formData.order ?? 1}
                                            onChange={(e) => {
                                                setFormData({ ...formData, order: e.target.value });
                                                if (formErrors.order) {
                                                    setFormErrors(prev => ({ ...prev, order: '' }));
                                                }
                                            }}
                                            onBlur={() => handleBlur('order')}
                                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green ${formErrors.order ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'}`}
                                            placeholder="Enter display order"
                                        />
                                        {formErrors.order && (
                                            <p className="text-red-500 text-sm mt-1">{formErrors.order}</p>
                                        )}
                                    </div>
                                )}

                                {modalFor === 'leadStatus' && modalType === 'create' && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Value Key <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.value || ''}
                                            onChange={(e) => {
                                                setFormData({ ...formData, value: e.target.value });
                                                if (formErrors.value) {
                                                    setFormErrors(prev => ({ ...prev, value: '' }));
                                                }
                                            }}
                                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green ${formErrors.value ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'}`}
                                            placeholder="Enter status value key (e.g., 'new', 'contacted')"
                                        />
                                        {formErrors.value && (
                                            <p className="text-red-500 text-sm mt-1">{formErrors.value}</p>
                                        )}
                                        <p className="text-xs text-gray-500 mt-1">
                                            Unique lowercase identifier for the status (e.g. 'new', 'contacted', 'in_progress').
                                        </p>
                                    </div>
                                )}

                                {/* Description field for Course Type, Document Type, Regularization Reason, Leave Type, Holiday Type, Policy Category, Source, Industry, Decision Maker, Experience, Skill, Requirement, Learning Path Category and Candidate Availability */}
                                {(modalFor === 'designation' || modalFor === 'courseType' || modalFor === 'documentType' || modalFor === 'regularizationReason' || modalFor === 'leaveType' || modalFor === 'holidayType' || modalFor === 'policyCategory' || modalFor === 'source' || modalFor === 'leadSource' || modalFor === 'leadDocumentType' || modalFor === 'leadCategory' || modalFor === 'activityStatus' || modalFor === 'leadStatus' || modalFor === 'leadVideoStatus' || modalFor === 'leadOrderStatus' || modalFor === 'leadCallStatus' || modalFor === 'industry' || modalFor === 'decisionMaker' || modalFor === 'experience' || modalFor === 'salaryComponent' || modalFor === 'skill' || modalFor === 'requirement' || modalFor === 'learningPathCategory' || modalFor === 'candidateAvailability' || modalFor === 'internalApprovalCategory') && (
                                    <>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Description
                                            </label>
                                            <textarea
                                                value={formData.description || ''}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    const limit = (modalFor === 'designation' || modalFor === 'documentType' || modalFor === 'leaveType' || modalFor === 'holidayType' || modalFor === 'policyCategory' || modalFor === 'source' || modalFor === 'leadSource' || modalFor === 'leadDocumentType' || modalFor === 'leadCategory' || modalFor === 'activityStatus' || modalFor === 'leadStatus' || modalFor === 'leadVideoStatus' || modalFor === 'leadOrderStatus' || modalFor === 'leadCallStatus' || modalFor === 'industry' || modalFor === 'decisionMaker' || modalFor === 'experience' || modalFor === 'salaryComponent' || modalFor === 'skill' || modalFor === 'requirement' || modalFor === 'learningPathCategory' || modalFor === 'internalTicketType' || modalFor === 'internalApprovalCategory') ? DOC_DESC_MAX_LENGTH : 500;
                                                    if (value.length <= limit) {
                                                        setFormData({ ...formData, description: value });
                                                    }
                                                }}
                                                maxLength={(modalFor === 'designation' || modalFor === 'documentType' || modalFor === 'leaveType' || modalFor === 'holidayType' || modalFor === 'policyCategory' || modalFor === 'source' || modalFor === 'leadSource' || modalFor === 'leadDocumentType' || modalFor === 'leadCategory' || modalFor === 'activityStatus' || modalFor === 'leadStatus' || modalFor === 'leadVideoStatus' || modalFor === 'leadOrderStatus' || modalFor === 'leadCallStatus' || modalFor === 'industry' || modalFor === 'decisionMaker' || modalFor === 'experience' || modalFor === 'salaryComponent' || modalFor === 'skill' || modalFor === 'requirement' || modalFor === 'learningPathCategory' || modalFor === 'internalTicketType' || modalFor === 'internalApprovalCategory') ? DOC_DESC_MAX_LENGTH : 500}
                                                rows={3}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green"
                                                placeholder="Enter description (optional)"
                                            />
                                            <p className={`text-xs text-right mt-1 ${(formData.description?.length || 0) >= ((modalFor === 'designation' || modalFor === 'documentType' || modalFor === 'leaveType' || modalFor === 'holidayType' || modalFor === 'policyCategory' || modalFor === 'source' || modalFor === 'leadSource' || modalFor === 'leadDocumentType' || modalFor === 'leadCategory' || modalFor === 'activityStatus' || modalFor === 'leadStatus' || modalFor === 'leadVideoStatus' || modalFor === 'leadOrderStatus' || modalFor === 'leadCallStatus' || modalFor === 'industry' || modalFor === 'decisionMaker' || modalFor === 'experience' || modalFor === 'salaryComponent' || modalFor === 'skill' || modalFor === 'requirement' || modalFor === 'learningPathCategory' || modalFor === 'internalTicketType' || modalFor === 'internalApprovalCategory') ? DOC_DESC_MAX_LENGTH : 500) ? 'text-red-500' : 'text-gray-400'}`}>
                                                {formData.description?.length || 0}/{(modalFor === 'designation' || modalFor === 'documentType' || modalFor === 'leaveType' || modalFor === 'holidayType' || modalFor === 'policyCategory' || modalFor === 'source' || modalFor === 'leadSource' || modalFor === 'leadDocumentType' || modalFor === 'leadCategory' || modalFor === 'activityStatus' || modalFor === 'leadStatus' || modalFor === 'leadVideoStatus' || modalFor === 'leadOrderStatus' || modalFor === 'leadCallStatus' || modalFor === 'industry' || modalFor === 'decisionMaker' || modalFor === 'experience' || modalFor === 'salaryComponent' || modalFor === 'skill' || modalFor === 'requirement' || modalFor === 'learningPathCategory' || modalFor === 'internalTicketType' || modalFor === 'internalApprovalCategory') ? DOC_DESC_MAX_LENGTH : 500}
                                            </p>
                                        </div>

                                        {/* Status Toggle - hidden for system-required items */}
                                        {!(modalType === 'edit' && editingItem?.isUndeletable) && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Status
                                            </label>
                                            <div className="flex items-center space-x-3">
                                                <button
                                                    type="button"
                                                    onClick={() => setFormData({ ...formData, isActive: !formData.isActive })}
                                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:ring-offset-2 ${formData.isActive ? 'bg-agrosaf-green' : 'bg-gray-200'
                                                        }`}
                                                >
                                                    <span
                                                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.isActive ? 'translate-x-6' : 'translate-x-1'
                                                            }`}
                                                    />
                                                </button>
                                                <span className={`text-sm font-medium ${formData.isActive ? 'text-green-700' : 'text-gray-500'}`}>
                                                    {formData.isActive ? 'Active' : 'Inactive'}
                                                </span>
                                            </div>
                                            <p className="text-xs text-gray-500 mt-1">
                                                {formData.isActive
                                                    ? 'This item will be available for selection'
                                                    : 'This item will be hidden from selection'}
                                            </p>
                                        </div>
                                        )}
                                        {modalType === 'edit' && editingItem?.isUndeletable && (
                                            <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                                                <p className="text-xs text-amber-700">
                                                    <strong>System Required:</strong> Status cannot be changed. You can only update the name and description.
                                                </p>
                                            </div>
                                        )}
                                    </>
                                )}

                                <div className="flex justify-end space-x-3 pt-4">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={closeModal}
                                        disabled={createMutation.isPending || updateMutation.isPending}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        variant="primary"
                                        disabled={createMutation.isPending || updateMutation.isPending}
                                        className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                                    >
                                        {createMutation.isPending || updateMutation.isPending ? 'Saving...' : (modalType === 'create' ? 'Create' : 'Update')}
                                    </Button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4">
                        <div className="fixed inset-0 bg-black opacity-50" onClick={closeDeleteModal}></div>
                        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                            <div className="flex items-center space-x-3 mb-4">
                                <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                                    <AlertTriangle className="h-5 w-5 text-red-600" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900">Confirm Delete</h3>
                            </div>

                            {deleteError ? (
                                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                                    <div className="flex items-start">
                                        <AlertTriangle className="h-5 w-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                                        <div className="flex-1">
                                            <p className="text-red-800 text-sm font-medium mb-2">{deleteError}</p>
                                            {(deletingItem?.type === 'courseType' || deletingItem?.type === 'documentType' || deletingItem?.type === 'regularizationReason' || deletingItem?.type === 'leaveType' || deletingItem?.type === 'holidayType' || deletingItem?.type === 'policyCategory') && deleteError.toLowerCase().includes('used') && (
                                                <div className="mt-3 pt-3 border-t border-red-200">
                                                    <p className="text-sm text-red-700 mb-2">
                                                        💡 <strong>Suggestion:</strong> Instead of deleting, you can deactivate this item to hide it from selection while preserving existing data.
                                                    </p>
                                                    <button
                                                        onClick={() => {
                                                            closeDeleteModal();
                                                            handleToggleStatus(deletingItem, deletingItem.type);
                                                        }}
                                                        className="text-sm text-blue-600 hover:text-blue-800 font-medium underline"
                                                    >
                                                        Deactivate this item instead
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <p className="text-gray-600 mb-4">
                                    Are you sure you want to delete "<span className="font-medium break-all">{deletingItem?.name}</span>"?
                                    <span className="block mt-2 text-sm text-gray-500">
                                        {deletingItem?.type === 'policyCategory'
                                            ? 'Are you sure you want to delete this policy category? This action cannot be undone.'
                                            : 'This action cannot be undone.'}
                                    </span>
                                </p>
                            )}

                            <div className="flex justify-end space-x-3">
                                <Button variant="outline" onClick={closeDeleteModal} disabled={deleteMutation.isPending}>
                                    Cancel
                                </Button>
                                {!deleteError && (
                                    <Button
                                        variant="danger"
                                        onClick={handleDelete}
                                        disabled={deleteMutation.isPending}
                                        className="bg-red-600 hover:bg-red-700 text-white"
                                    >
                                        {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk Delete Confirmation Modal */}
            {showBulkDeleteModal && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4">
                        <div className="fixed inset-0 bg-black opacity-50" onClick={() => setShowBulkDeleteModal(false)}></div>
                        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                            <div className="flex items-center space-x-3 mb-4">
                                <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                                    <AlertTriangle className="h-5 w-5 text-red-600" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900">Confirm Bulk Delete</h3>
                            </div>

                            <p className="text-gray-600 mb-4">
                                Are you sure you want to delete <span className="font-bold">{selectedRows.size}</span> selected items?
                                <span className="block mt-2 text-sm text-gray-500">
                                    This action cannot be undone and will affect all selected items.
                                </span>
                            </p>

                            <div className="flex justify-end space-x-3">
                                <Button variant="outline" onClick={() => setShowBulkDeleteModal(false)} disabled={bulkDeleteMutation.isPending}>
                                    Cancel
                                </Button>
                                <Button
                                    variant="danger"
                                    onClick={handleBulkDelete}
                                    disabled={bulkDeleteMutation.isPending}
                                    className="bg-red-600 hover:bg-red-700 text-white"
                                >
                                    {bulkDeleteMutation.isPending ? 'Deleting...' : 'Delete Selected'}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* View Detail Modal */}
            {showViewModal && viewingItem && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4">
                        <div className="fixed inset-0 bg-black opacity-50" onClick={closeViewModal}></div>
                        <div className="relative bg-white rounded-lg shadow-xl max-w-xl w-full p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-2xl font-semibold text-gray-900 flex items-center">
                                    {viewingItem.type === 'industry' && <Building2 className="h-6 w-6 mr-2 text-agrosaf-green" />}
                                    {viewingItem.type === 'decisionMaker' && <Users className="h-6 w-6 mr-2 text-agrosaf-green" />}
                                    {viewingItem.type === 'designation' && <Briefcase className="h-6 w-6 mr-2 text-agrosaf-green" />}
                                    {viewingItem.type === 'courseType' && <GraduationCap className="h-6 w-6 mr-2 text-agrosaf-green" />}
                                    {viewingItem.type === 'documentType' && <FileText className="h-6 w-6 mr-2 text-agrosaf-green" />}
                                    {viewingItem.type === 'regularizationReason' && <AlertTriangle className="h-6 w-6 mr-2 text-agrosaf-green" />}
                                    {viewingItem.type === 'leaveType' && <Calendar className="h-6 w-6 mr-2 text-agrosaf-green" />}
                                    {viewingItem.type === 'holidayType' && <Calendar className="h-6 w-6 mr-2 text-agrosaf-green" />}
                                    {viewingItem.type === 'policyCategory' && <Database className="h-6 w-6 mr-2 text-agrosaf-green" />}
                                    {viewingItem.type === 'source' && <RefreshCw className="h-6 w-6 mr-2 text-agrosaf-green" />}
                                    {viewingItem.type === 'leadSource' && <Users className="h-6 w-6 mr-2 text-agrosaf-green" />}
                                    {viewingItem.type === 'leadDocumentType' && <FileText className="h-6 w-6 mr-2 text-agrosaf-green" />}
                                    {viewingItem.type === 'leadCategory' && <FileText className="h-6 w-6 mr-2 text-agrosaf-green" />}
                                    {viewingItem.type === 'activityStatus' && <FileText className="h-6 w-6 mr-2 text-agrosaf-green" />}
                                    {viewingItem.type === 'leadStatus' && <FileText className="h-6 w-6 mr-2 text-agrosaf-green" />}
                                    {viewingItem.type === 'leadVideoStatus' && <FileText className="h-6 w-6 mr-2 text-agrosaf-green" />}
                                    {viewingItem.type === 'leadOrderStatus' && <FileText className="h-6 w-6 mr-2 text-agrosaf-green" />}
                                    {viewingItem.type === 'leadCallStatus' && <Phone className="h-6 w-6 mr-2 text-agrosaf-green" />}
                                    {viewingItem.type === 'experience' && <Briefcase className="h-6 w-6 mr-2 text-agrosaf-green" />}
                                    {viewingItem.type === 'requirement' && <FileText className="h-6 w-6 mr-2 text-agrosaf-green" />}
                                    {viewingItem.type === 'learningPathCategory' && <FileText className="h-6 w-6 mr-2 text-agrosaf-green" />}
                                    {viewingItem.type === 'candidateAvailability' && <Users className="h-6 w-6 mr-2 text-agrosaf-green" />}
                                    {viewingItem.type === 'internalTicketType' && <FileText className="h-6 w-6 mr-2 text-agrosaf-green" />}
                                    {viewingItem.type === 'internalApprovalCategory' && <Database className="h-6 w-6 mr-2 text-agrosaf-green" />}
                                    {viewingItem.type === 'industry' ? 'Industry' : viewingItem.type === 'decisionMaker' ? 'Decision Maker' : viewingItem.type === 'designation' ? 'Designation' : viewingItem.type === 'courseType' ? 'Course Type' : viewingItem.type === 'documentType' ? 'Document Type' : viewingItem.type === 'leaveType' ? 'Leave Type' : viewingItem.type === 'holidayType' ? 'Holiday Type' : viewingItem.type === 'policyCategory' ? 'Policy Category' : viewingItem.type === 'source' ? 'Source' : viewingItem.type === 'leadSource' ? 'Lead Source' : viewingItem.type === 'leadDocumentType' ? 'Lead Document Type' : viewingItem.type === 'leadCategory' ? 'Lead Category' : viewingItem.type === 'activityStatus' ? 'Activity Status' : viewingItem.type === 'leadStatus' ? 'Lead Status' : viewingItem.type === 'leadVideoStatus' ? 'Lead Video Status' : viewingItem.type === 'leadOrderStatus' ? 'Lead Order Status' : viewingItem.type === 'leadCallStatus' ? 'Lead Call Status' : viewingItem.type === 'experience' ? 'Job Experience' : viewingItem.type === 'skill' ? 'Skill' : viewingItem.type === 'requirement' ? 'Requirement' : viewingItem.type === 'learningPathCategory' ? 'Learning Path Category' : viewingItem.type === 'candidateAvailability' ? 'Candidate Availability' : viewingItem.type === 'internalTicketType' ? 'Ticket Type' : viewingItem.type === 'internalApprovalCategory' ? 'Approval Category' : 'Regularization Reason'} Details
                                </h3>
                                <button onClick={closeViewModal} className="text-gray-400 hover:text-gray-600">
                                    <X className="h-6 w-6" />
                                </button>
                            </div>

                            <div className="space-y-6">
                                {/* Basic Information */}
                                <div>
                                    <h4 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Basic Information</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="md:col-span-2">
                                            <label className="block text-sm font-medium text-gray-500 mb-1">
                                                {viewingItem.type === 'industry' ? 'Industry Code' : viewingItem.type === 'decisionMaker' ? 'Decision Maker Code' : viewingItem.type === 'designation' ? 'Designation Code' : viewingItem.type === 'courseType' ? 'Course Type ID' : viewingItem.type === 'documentType' ? 'Document Type Code' : viewingItem.type === 'leaveType' ? 'Leave Type Code' : viewingItem.type === 'holidayType' ? 'Holiday Type Code' : viewingItem.type === 'policyCategory' ? 'Category Code' : viewingItem.type === 'source' ? 'Source Code' : viewingItem.type === 'leadSource' ? 'Lead Source Code' : viewingItem.type === 'leadDocumentType' ? 'Lead Document Type Code' : viewingItem.type === 'leadCategory' ? 'Lead Category Code' : viewingItem.type === 'activityStatus' ? 'Activity Status Code' : viewingItem.type === 'leadStatus' ? 'Value Key' : viewingItem.type === 'leadVideoStatus' ? 'Lead Video Status Code' : viewingItem.type === 'leadOrderStatus' ? 'Lead Order Status Code' : viewingItem.type === 'leadCallStatus' ? 'Lead Call Status Code' : viewingItem.type === 'experience' ? 'Job Experience Code' : viewingItem.type === 'skill' ? 'Skill Code' : viewingItem.type === 'requirement' ? 'Requirement Code' : viewingItem.type === 'learningPathCategory' ? 'Category Code' : viewingItem.type === 'candidateAvailability' ? 'Availability Code' : viewingItem.type === 'internalTicketType' ? 'Ticket Type Code' : viewingItem.type === 'internalApprovalCategory' ? 'Category Code' : 'Reason Code'}
                                            </label>
                                            <p className="text-sm font-mono text-gray-900 bg-gray-50 p-2 rounded border border-gray-200">
                                                {viewingItem.type === 'industry' ? viewingItem.industryCode : viewingItem.type === 'decisionMaker' ? viewingItem.decisionMakerCode : viewingItem.type === 'designation' ? viewingItem.designationCode : viewingItem.type === 'courseType' ? viewingItem.courseTypeId : viewingItem.type === 'documentType' ? viewingItem.documentTypeCode : viewingItem.type === 'leaveType' ? viewingItem.leaveTypeCode : viewingItem.type === 'holidayType' ? viewingItem.holidayTypeCode : viewingItem.type === 'policyCategory' ? viewingItem.code : viewingItem.type === 'source' ? viewingItem.sourceCode : viewingItem.type === 'leadSource' ? viewingItem.leadSourceCode : viewingItem.type === 'leadDocumentType' ? viewingItem.leadDocumentTypeCode : viewingItem.type === 'leadCategory' ? viewingItem.leadCategoryCode : viewingItem.type === 'activityStatus' ? viewingItem.activityStatusCode : viewingItem.type === 'leadStatus' ? viewingItem.value : viewingItem.type === 'leadVideoStatus' ? viewingItem.leadVideoStatusCode : viewingItem.type === 'leadOrderStatus' ? viewingItem.leadOrderStatusCode : viewingItem.type === 'leadCallStatus' ? viewingItem.leadCallStatusCode : viewingItem.type === 'experience' ? viewingItem.experienceCode : viewingItem.type === 'skill' ? viewingItem.skillCode : viewingItem.type === 'requirement' ? viewingItem.requirementCode : viewingItem.type === 'learningPathCategory' ? viewingItem.categoryCode : viewingItem.type === 'candidateAvailability' ? viewingItem.availabilityCode : viewingItem.type === 'internalTicketType' ? viewingItem.ticketTypeCode : viewingItem.type === 'internalApprovalCategory' ? viewingItem.categoryCode : viewingItem.reasonCode}
                                            </p>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-500 mb-1">
                                                {viewingItem.type === 'industry' ? 'Industry Name' : viewingItem.type === 'decisionMaker' ? 'Decision Maker Name' : viewingItem.type === 'designation' ? 'Designation Name' : viewingItem.type === 'courseType' ? 'Course Type Name' : viewingItem.type === 'documentType' ? 'Document Type Name' : viewingItem.type === 'regularizationReason' ? 'Reason Name' : viewingItem.type === 'leaveType' ? 'Leave Type Name' : viewingItem.type === 'holidayType' ? 'Holiday Type Name' : viewingItem.type === 'policyCategory' ? 'Category Name' : viewingItem.type === 'source' ? 'Source Name' : viewingItem.type === 'leadSource' ? 'Lead Source Name' : viewingItem.type === 'leadDocumentType' ? 'Lead Document Type Name' : viewingItem.type === 'leadCategory' ? 'Lead Category Name' : viewingItem.type === 'activityStatus' ? 'Activity Status Name' : viewingItem.type === 'leadStatus' ? 'Lead Status Name' : viewingItem.type === 'leadVideoStatus' ? 'Lead Video Status Name' : viewingItem.type === 'leadOrderStatus' ? 'Lead Order Status Name' : viewingItem.type === 'leadCallStatus' ? 'Lead Call Status Name' : viewingItem.type === 'experience' ? 'Job Experience Name' : viewingItem.type === 'skill' ? 'Skill Name' : viewingItem.type === 'requirement' ? 'Requirement Name' : viewingItem.type === 'learningPathCategory' ? 'Category Name' : viewingItem.type === 'candidateAvailability' ? 'Availability Name' : viewingItem.type === 'internalTicketType' ? 'Ticket Type Name' : viewingItem.type === 'internalApprovalCategory' ? 'Category Name' : 'Name'}
                                            </label>
                                            <p className="text-sm font-medium text-gray-900 bg-gray-50 p-2 rounded border border-gray-200 break-words capitalize">
                                                {viewingItem.type === 'holidayType' ? viewingItem.name.toUpperCase() : viewingItem.name}
                                            </p>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-500 mb-1">
                                                Status
                                            </label>
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${viewingItem.isActive
                                                ? 'bg-green-100 text-green-800'
                                                : 'bg-red-100 text-red-800'
                                                }`}>
                                                {viewingItem.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                        {viewingItem.type === 'designation' && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-500 mb-1">
                                                    Order
                                                </label>
                                                <p className="text-sm font-medium text-gray-900 bg-gray-50 p-2 rounded border border-gray-200">
                                                    {viewingItem.order ?? 1}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {(viewingItem.type === 'designation' || viewingItem.type === 'courseType' || viewingItem.type === 'documentType' || viewingItem.type === 'regularizationReason' || viewingItem.type === 'leaveType' || viewingItem.type === 'holidayType' || viewingItem.type === 'policyCategory' || viewingItem.type === 'source' || viewingItem.type === 'leadSource' || viewingItem.type === 'leadDocumentType' || viewingItem.type === 'leadCategory' || viewingItem.type === 'activityStatus' || viewingItem.type === 'leadStatus' || viewingItem.type === 'leadVideoStatus' || viewingItem.type === 'industry' || viewingItem.type === 'decisionMaker' || viewingItem.type === 'experience' || viewingItem.type === 'skill' || viewingItem.type === 'requirement' || viewingItem.type === 'learningPathCategory' || viewingItem.type === 'internalTicketType' || viewingItem.type === 'internalApprovalCategory') && (
                                    <div>
                                        <h4 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Description</h4>
                                        <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded border border-gray-200 break-words first-letter:capitalize">
                                            {viewingItem.description || 'No description provided'}
                                        </p>
                                    </div>
                                )}

                                {/* Timestamps */}
                                {(viewingItem.createdAt || viewingItem.updatedAt || viewingItem._id || viewingItem.id) && (
                                    <div className="pt-4 border-t border-gray-100">
                                        <h4 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Timestamps</h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {(viewingItem.createdAt || viewingItem._id || viewingItem.id) && (
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-500 mb-1">Created At</label>
                                                    <p className="text-sm text-gray-900">
                                                        {formatDate(getItemDate(viewingItem))}
                                                    </p>
                                                </div>
                                            )}
                                            {viewingItem.updatedAt && (
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-500 mb-1">Updated At</label>
                                                    <p className="text-sm text-gray-900">
                                                        {formatDate(viewingItem.updatedAt)}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="flex justify-end pt-6">
                                <Button variant="outline" onClick={closeViewModal}>
                                    Close
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ConfigureManagement;
